<template>
  <div class="event-config-wrap">
    <!-- 顶部标题 -->
    <div class="event-title">
      <i class="el-icon-arrow-left" @click="handleGoBack"></i>
      <span>{{ pageTitle }}</span>
    </div>

    <!-- 产品信息 -->
    <div class="section-container">
      <div class="section-title">产品信息</div>
      <div class="product-info-row">
        <span class="field-label">
          <span class="required-mark">*</span>
          上报产品
        </span>
        <div class="product-select-area">
          <product-select
            multiple
            :value.sync="formData.products"
            placeholder="请选择产品"
            :isDefault="false"
            :disabled="isViewMode || mode === 'edit'"
            popper-class="BI-select BI-select-product"
          ></product-select>
          <span v-if="formData.products.length > 0" class="selected-summary">
            已选 {{ formData.products.length }} 个产品
          </span>
        </div>
      </div>
    </div>

    <!-- 事件定义 -->
    <div class="section-container event-definitions-section">
      <div class="section-header-row">
        <span class="section-title">事件定义</span>
        <span class="section-meta">共 {{ eventDefinitions.length }} 个事件</span>
      </div>

      <div v-for="(eventDef, defIndex) in eventDefinitions" :key="defIndex" class="event-definition-card">
        <!-- 卡片 Toolbar -->
        <div class="event-card-toolbar">
          <button type="button" class="event-card-toggle" @click="toggleEventCollapse(defIndex)">
            <i :class="eventDef.collapsed ? 'el-icon-caret-right' : 'el-icon-caret-bottom'"></i>
          </button>

          <div class="toolbar-field-group toolbar-name-field">
            <label class="toolbar-field-label">
              <span class="required-mark">*</span>
              事件名称
            </label>
            <div class="toolbar-input-wrap">
              <el-input
                v-model="eventDef.name"
                :maxlength="40"
                placeholder="请输入事件名称"
                class="toolbar-name-input"
                :disabled="isViewMode"
              />
              <span class="char-count">{{ (eventDef.name || '').length }}/40</span>
            </div>
          </div>

          <div class="toolbar-field-group toolbar-platform-field toolbar-divider">
            <label class="toolbar-field-label">
              <span class="required-mark">*</span>
              上报平台
            </label>
            <el-checkbox-group v-model="eventDef.reportPlatforms" class="report-platforms">
              <el-checkbox
                v-for="platform in reportPlatformOptions"
                :key="platform.value"
                :label="platform.value"
                :disabled="isViewMode || isReportPlatformDisabled(eventDef, platform.value)"
              >
                {{ platform.label }}
              </el-checkbox>
            </el-checkbox-group>
          </div>

          <div class="toolbar-field-group toolbar-report-window-field toolbar-divider">
            <label class="toolbar-field-label">上报窗口</label>
            <el-switch
              v-model="eventDef.reportWindowEnabled"
              :disabled="isViewMode"
              @change="onReportWindowChange(eventDef)"
            />
            <template v-if="eventDef.reportWindowEnabled">
              <span class="toolbar-inline-text">新增</span>
              <el-input
                v-model="eventDef.reportWindowHours"
                class="toolbar-report-window-input"
                :disabled="isViewMode"
                @input="onReportWindowHoursInput(eventDef)"
                @blur="onReportWindowHoursBlur(eventDef)"
              />
              <span class="toolbar-inline-text">小时后开始上报</span>
            </template>
          </div>

          <div class="toolbar-field-group toolbar-remark-field toolbar-divider">
            <label class="toolbar-field-label">备注</label>
            <div class="toolbar-input-wrap">
              <el-input
                v-model="eventDef.remark"
                :maxlength="200"
                placeholder="选填"
                class="toolbar-remark-input"
                :disabled="isViewMode"
              />
              <span class="char-count">{{ (eventDef.remark || '').length }}/200</span>
            </div>
          </div>

          <button
            v-if="!isViewMode && eventDefinitions.length > 1"
            type="button"
            class="event-card-close-btn"
            title="删除此事件"
            @click="deleteEventDef(defIndex)"
          >
            <i class="el-icon-error"></i>
          </button>
        </div>

        <!-- 卡片 Body -->
        <div v-show="!eventDef.collapsed" class="event-card-body">
          <div class="event-def-row-cols">
            <!-- 用户属性 -->
            <div class="event-def-col event-def-col-attrs">
              <div class="definition-block">
                <h3 class="definition-subtitle">用户属性</h3>
                <div class="condition-content">
                  <div class="condition-list-wrapper" v-if="eventDef.userAttributes.length > 0">
                    <div
                      v-if="eventDef.userAttributes.length >= 2"
                      class="condition-badge-center"
                      :class="{ clickable: !isViewMode }"
                      @click="!isViewMode && toggleUserAttrLogic(defIndex)"
                    >
                      {{ eventDef.userLogicType === 'and' ? '且' : '或' }}
                    </div>
                    <div class="condition-list">
                      <div
                        v-for="(attr, attrIndex) in eventDef.userAttributes"
                        :key="'ua-' + defIndex + '-' + attrIndex"
                        class="condition-item"
                      >
                        <div class="condition-row">
                          <div class="condition-selects">
                            <el-select
                              v-model="attr.typeValue"
                              placeholder="请选择"
                              class="condition-select"
                              @change="onUserAttrTypeChange(defIndex, attr, attrIndex)"
                              :disabled="isViewMode"
                            >
                              <el-option
                                v-for="type in getAvailableUserAttrTypes(defIndex, attrIndex)"
                                :key="type.value"
                                :label="type.label"
                                :value="type.value"
                              />
                            </el-select>

                            <el-select
                              v-if="getUserAttrConfig(attr.typeValue) && getUserAttrConfig(attr.typeValue).hasOperator"
                              v-model="attr.operatorValue"
                              placeholder="请选择"
                              class="condition-select"
                              :disabled="isViewMode"
                            >
                              <el-option
                                v-for="op in getUserAttrConfig(attr.typeValue).operators"
                                :key="op.value"
                                :label="op.label"
                                :value="op.value"
                              />
                            </el-select>

                            <el-select
                              v-if="
                                getUserAttrConfig(attr.typeValue) &&
                                getUserAttrConfig(attr.typeValue).hasValue &&
                                getUserAttrConfig(attr.typeValue).valueType === 'select'
                              "
                              v-model="attr.value"
                              placeholder="请选择"
                              class="condition-select"
                              style="min-width: 120px !important"
                              :disabled="isViewMode"
                              :multiple="isMultipleUserAttr(attr.typeValue)"
                              collapse-tags
                              clearable
                            >
                              <el-option
                                v-for="val in getUserAttrConfig(attr.typeValue).valueOptions"
                                :key="val.value"
                                :label="val.label"
                                :value="val.value"
                              />
                            </el-select>

                            <div v-if="attr.typeValue === 'retention_user'" class="retention-input-wrapper">
                              <el-input
                                v-model="attr.value"
                                :disabled="isViewMode"
                                class="retention-input-number"
                                type="number"
                              />
                              <span class="retention-text">留</span>
                            </div>
                          </div>
                          <el-button
                            v-if="!isViewMode"
                            type="text"
                            class="delete-btn"
                            @click="deleteUserAttribute(defIndex, attrIndex)"
                          >
                            <i class="el-icon-error"></i>
                          </el-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <el-button v-if="!isViewMode" size="small" plain class="add-btn" @click="addUserAttribute(defIndex)">
                  <i class="el-icon-plus"></i>
                  添加
                </el-button>
              </div>
            </div>

            <!-- 行为条件 -->
            <div class="event-def-col event-def-col-conditions">
              <div class="definition-block definition-block-last">
                <h3 class="definition-subtitle">行为条件</h3>
                <div class="condition-content">
                  <div class="condition-list-wrapper" v-if="eventDef.behaviorGroups.length > 0">
                    <!-- 条件组间 且/或 标记 -->
                    <div
                      v-if="eventDef.behaviorGroups.length >= 2"
                      class="condition-badge-center"
                      :class="{ clickable: !isViewMode }"
                      @click="!isViewMode && toggleBehaviorGroupLogic(defIndex)"
                    >
                      {{ eventDef.behaviorGroupLogicType === 'and' ? '且' : '或' }}
                    </div>
                    <div class="condition-list">
                      <div
                        v-for="(group, groupIndex) in eventDef.behaviorGroups"
                        :key="'group-' + defIndex + '-' + groupIndex"
                        class="condition-item"
                      >
                        <!-- 条件组盒子 -->
                        <div class="behavior-group-box">
                          <div class="behavior-group-header">
                            <span class="behavior-group-title">条件组</span>
                            <el-button
                              v-if="!isViewMode"
                              type="text"
                              class="delete-btn"
                              @click="deleteBehaviorGroup(defIndex, groupIndex)"
                            >
                              <i class="el-icon-error"></i>
                            </el-button>
                          </div>

                          <!-- 组内条件列表 -->
                          <div class="condition-content">
                            <div class="condition-list-wrapper" v-if="group.conditions.length > 0">
                              <div
                                v-if="group.conditions.length >= 2"
                                class="condition-badge-center"
                                :class="{ clickable: !isViewMode }"
                                @click="!isViewMode && toggleGroupConditionLogic(defIndex, groupIndex)"
                              >
                                {{ group.logicType === 'and' ? '且' : '或' }}
                              </div>
                              <div class="condition-list">
                                <div
                                  v-for="(bc, bcIndex) in group.conditions"
                                  :key="'bc-' + defIndex + '-' + groupIndex + '-' + bcIndex"
                                  class="condition-item"
                                >
                                  <!-- 主行为条件行 -->
                                  <div class="condition-row">
                                    <div class="condition-selects">
                                      <div class="window-period-control">
                                        <el-select
                                          v-model="bc.windowType"
                                          placeholder="请选择"
                                          class="condition-select window-type-select"
                                          :disabled="isViewMode"
                                          @change="onWindowTypeChange(bc)"
                                        >
                                          <el-option
                                            v-for="type in windowTypeOptions"
                                            :key="type.value"
                                            :label="type.label"
                                            :value="type.value"
                                          />
                                        </el-select>
                                        <template v-if="bc.windowType !== 'unlimited'">
                                          <el-autocomplete
                                            v-model="bc.windowDays"
                                            :fetch-suggestions="queryWindowDaySuggestions"
                                            placeholder="天数"
                                            class="window-days-input"
                                            :disabled="isViewMode"
                                            @select="onWindowDaysSelect(bc, $event)"
                                          />
                                          <span class="window-day-text">日</span>
                                        </template>
                                      </div>

                                      <el-select
                                        v-if="!bc.showCalculator"
                                        v-model="bc.behaviorTypeKey"
                                        placeholder="请选择"
                                        class="condition-select"
                                        :disabled="isViewMode"
                                        @change="onBehaviorTypeChange(eventDef, bc)"
                                      >
                                        <el-option
                                          v-for="b in behaviorTypes"
                                          :key="b.typeKey"
                                          :label="b.label"
                                          :value="b.typeKey"
                                        />
                                      </el-select>
                                      <span v-else class="custom-text">自定义</span>

                                      <!-- 增量带值收入 -->
                                      <template v-if="isIncrementRevenueBehavior(bc)">
                                        <span class="special-text">每 &gt;=</span>
                                        <el-input
                                          type="number"
                                          v-model="bc.value"
                                          :min="1"
                                          :step="1"
                                          :disabled="isViewMode"
                                          controls-position="right"
                                          class="revenue-input-number"
                                        />
                                        <span class="special-text">美元</span>
                                        <el-tooltip placement="top" effect="light" popper-class="revenue-rule-tooltip">
                                          <div slot="content" class="revenue-rule-tooltip-content">
                                            {{ getRevenueBehaviorTip(bc) }}
                                          </div>
                                          <i class="el-icon-question revenue-rule-icon"></i>
                                        </el-tooltip>
                                      </template>

                                      <!-- 累计收入 -->
                                      <template v-else-if="isAccumulatedRevenueBehavior(bc)">
                                        <el-select
                                          v-model="bc.operatorValue"
                                          placeholder="请选择"
                                          class="condition-select"
                                          :disabled="isViewMode"
                                        >
                                          <el-option
                                            v-for="op in operators"
                                            :key="op.value"
                                            :label="op.label"
                                            :value="op.value"
                                          />
                                        </el-select>
                                        <el-input
                                          v-if="isCollectionOperator(bc.operatorValue)"
                                          v-model="bc.value"
                                          placeholder="多个值用逗号分隔"
                                          class="condition-input condition-collection-input"
                                          :disabled="isViewMode"
                                        />
                                        <el-input
                                          v-else-if="!isRangeOperator(bc.operatorValue)"
                                          v-model="bc.value"
                                          placeholder="请输入"
                                          class="condition-input"
                                          type="number"
                                          :disabled="isViewMode"
                                        />
                                        <template v-if="isRangeOperator(bc.operatorValue)">
                                          <el-input
                                            v-model="bc.value"
                                            placeholder="最小值"
                                            class="condition-input"
                                            type="number"
                                            :disabled="isViewMode"
                                          />
                                          <span class="range-separator">-</span>
                                          <el-input
                                            v-model="bc.value2"
                                            placeholder="最大值"
                                            class="condition-input"
                                            type="number"
                                            :disabled="isViewMode"
                                          />
                                        </template>
                                        <span class="special-text">美元</span>
                                        <el-tooltip placement="top" effect="light" popper-class="revenue-rule-tooltip">
                                          <div slot="content" class="revenue-rule-tooltip-content">
                                            {{ getRevenueBehaviorTip(bc) }}
                                          </div>
                                          <i class="el-icon-question revenue-rule-icon"></i>
                                        </el-tooltip>
                                      </template>

                                      <!-- 其他行为类型 -->
                                      <template v-else>
                                        <el-select
                                          v-model="bc.operatorValue"
                                          placeholder="请选择"
                                          class="condition-select"
                                          :disabled="isViewMode"
                                        >
                                          <el-option
                                            v-for="op in operators"
                                            :key="op.value"
                                            :label="op.label"
                                            :value="op.value"
                                          />
                                        </el-select>

                                        <el-input
                                          v-if="isCollectionOperator(bc.operatorValue)"
                                          v-model="bc.value"
                                          placeholder="多个值用逗号分隔"
                                          class="condition-input condition-collection-input"
                                          :disabled="isViewMode"
                                        />
                                        <el-input
                                          v-else-if="!isRangeOperator(bc.operatorValue)"
                                          v-model="bc.value"
                                          placeholder="请输入"
                                          class="condition-input"
                                          type="number"
                                          :disabled="isViewMode"
                                        />
                                        <span v-if="!isRangeOperator(bc.operatorValue) && getBehaviorUnitText(bc)" class="special-text">
                                          {{ getBehaviorUnitText(bc) }}
                                        </span>

                                        <template v-if="isRangeOperator(bc.operatorValue)">
                                          <el-input
                                            v-model="bc.value"
                                            placeholder="最小值"
                                            class="condition-input"
                                            type="number"
                                            :disabled="isViewMode"
                                          />
                                          <span class="range-separator">-</span>
                                          <el-input
                                            v-model="bc.value2"
                                            placeholder="最大值"
                                            class="condition-input"
                                            type="number"
                                            :disabled="isViewMode"
                                          />
                                          <span v-if="getBehaviorUnitText(bc)" class="special-text">
                                            {{ getBehaviorUnitText(bc) }}
                                          </span>
                                        </template>
                                      </template>

                                      <!-- 操作图标 -->
                                      <svg-icon
                                        v-if="!isRevenueBehavior(bc)"
                                        icon-class="calculator"
                                        class="opera-icon"
                                        :class="{ 'active-icon': bc.showCalculator }"
                                        @click.native="toggleCalculator(defIndex, groupIndex, bcIndex)"
                                      />
                                    </div>

                                    <!-- 筛选按钮 -->
                                    <el-button
                                      v-if="!isViewMode && isSubFilterableBehavior(bc)"
                                      class="filter-btn"
                                      @click="addSubFilter(defIndex, groupIndex, bcIndex)"
                                    >
                                      筛选
                                    </el-button>

                                    <el-tooltip
                                      v-if="!isViewMode || (bc.countryThresholds && bc.countryThresholds.length > 0)"
                                      :content="
                                        bc.countryThresholds && bc.countryThresholds.length > 0
                                          ? `地区阈值（已配置 ${bc.countryThresholds.length} 个地区）`
                                          : '配置地区阈值'
                                      "
                                      placement="top"
                                      effect="dark"
                                    >
                                      <el-button
                                        class="country-threshold-toggle"
                                        :class="{
                                          'is-active': bc.showCountryThresholds,
                                          'has-config': bc.countryThresholds && bc.countryThresholds.length > 0,
                                        }"
                                        :aria-label="
                                          bc.countryThresholds && bc.countryThresholds.length > 0
                                            ? `地区阈值，已配置 ${bc.countryThresholds.length} 个地区`
                                            : '配置地区阈值'
                                        "
                                        @click="!isViewMode && toggleCountryThresholds(bc)"
                                      >
                                        <svg-icon icon-class="globe" class="country-threshold-globe" />
                                        <span
                                          v-if="bc.countryThresholds && bc.countryThresholds.length > 0"
                                          class="country-threshold-count"
                                        >
                                          {{ bc.countryThresholds.length }}
                                        </span>
                                      </el-button>
                                    </el-tooltip>

                                    <!-- 删除按钮 -->
                                    <el-button
                                      v-if="!isViewMode"
                                      type="text"
                                      class="delete-btn"
                                      @click="deleteConditionFromGroup(defIndex, groupIndex, bcIndex)"
                                    >
                                      <i class="el-icon-error"></i>
                                    </el-button>
                                  </div>

                                  <!-- 国家地区阈值覆盖 -->
                                  <div
                                    v-if="bc.showCountryThresholds || (isViewMode && bc.countryThresholds && bc.countryThresholds.length > 0)"
                                    class="country-threshold-panel"
                                    :class="{
                                      'is-range-threshold': isRangeOperator(getCountryThresholdOperator(bc)),
                                      'is-collection-threshold': isCollectionOperator(getCountryThresholdOperator(bc)),
                                    }"
                                  >
                                    <div class="country-threshold-table">
                                      <div class="country-threshold-table-row country-threshold-table-header">
                                        <div class="country-threshold-country-cell">国家地区</div>
                                        <div class="country-threshold-value-cell">阈值</div>
                                        <div class="country-threshold-action-cell"></div>
                                      </div>
                                      <div class="country-threshold-table-row country-threshold-default-row">
                                        <div class="country-threshold-country-cell">default</div>
                                        <div class="country-threshold-value-cell">
                                          <template v-if="isRangeOperator(getCountryThresholdOperator(bc))">
                                            <el-input
                                              v-model="bc.value"
                                              type="number"
                                              placeholder="最小值"
                                              class="country-threshold-input"
                                              :disabled="isViewMode"
                                            />
                                            <span class="range-separator">-</span>
                                            <el-input
                                              v-model="bc.value2"
                                              type="number"
                                              placeholder="最大值"
                                              class="country-threshold-input"
                                              :disabled="isViewMode"
                                            />
                                          </template>
                                        <el-input
                                          v-else-if="isCollectionOperator(getCountryThresholdOperator(bc))"
                                          v-model="bc.value"
                                          placeholder="多个值用逗号分隔"
                                          class="country-threshold-input country-threshold-collection-input"
                                          :disabled="isViewMode"
                                        />
                                        <el-input
                                          v-else
                                          v-model="bc.value"
                                          type="number"
                                          placeholder="请输入阈值"
                                          class="country-threshold-input"
                                          :disabled="isViewMode"
                                        />
                                          <span v-if="getBehaviorUnitText(bc)" class="country-threshold-unit">
                                            {{ getBehaviorUnitText(bc) }}
                                          </span>
                                        </div>
                                        <div class="country-threshold-action-cell"></div>
                                      </div>
                                      <div
                                        v-for="(countryThreshold, countryIndex) in bc.countryThresholds"
                                        :key="'country-threshold-' + countryIndex"
                                        class="country-threshold-table-row"
                                      >
                                        <div class="country-threshold-country-cell">
                                          <el-select
                                            v-model="countryThreshold.country"
                                            filterable
                                            placeholder="请选择国家地区"
                                            class="country-threshold-select"
                                            :disabled="isViewMode"
                                          >
                                            <el-option
                                              v-for="country in getAvailableCountryThresholdCountries(bc, countryIndex)"
                                              :key="country.value"
                                              :label="country.label"
                                              :value="country.value"
                                            />
                                          </el-select>
                                        </div>
                                        <div class="country-threshold-value-cell">
                                          <template v-if="isRangeOperator(getCountryThresholdOperator(bc))">
                                            <el-input
                                              v-model="countryThreshold.value"
                                              type="number"
                                              placeholder="最小值"
                                              class="country-threshold-input"
                                              :disabled="isViewMode"
                                            />
                                            <span class="range-separator">-</span>
                                            <el-input
                                              v-model="countryThreshold.value2"
                                              type="number"
                                              placeholder="最大值"
                                              class="country-threshold-input"
                                              :disabled="isViewMode"
                                            />
                                          </template>
                                          <el-input
                                            v-else-if="isCollectionOperator(getCountryThresholdOperator(bc))"
                                            v-model="countryThreshold.value"
                                            placeholder="多个值用逗号分隔"
                                            class="country-threshold-input country-threshold-collection-input"
                                            :disabled="isViewMode"
                                          />
                                          <el-input
                                            v-else
                                            v-model="countryThreshold.value"
                                            type="number"
                                            placeholder="请输入阈值"
                                            class="country-threshold-input"
                                            :disabled="isViewMode"
                                          />
                                          <span v-if="getBehaviorUnitText(bc)" class="country-threshold-unit">
                                            {{ getBehaviorUnitText(bc) }}
                                          </span>
                                        </div>
                                        <div class="country-threshold-action-cell">
                                          <el-button
                                            v-if="!isViewMode"
                                            type="text"
                                            class="country-threshold-remove"
                                            aria-label="删除地区阈值"
                                            @click="removeCountryThreshold(bc, countryIndex)"
                                          >
                                            <i class="el-icon-error"></i>
                                          </el-button>
                                        </div>
                                      </div>
                                    </div>
                                    <el-button
                                      v-if="!isViewMode"
                                      size="small"
                                      plain
                                      class="add-btn country-threshold-add"
                                      @click="addCountryThreshold(bc)"
                                    >
                                      <i class="el-icon-plus"></i>
                                      添加地区
                                    </el-button>
                                  </div>

                                  <!-- 公式编辑面板 -->
                                  <div v-if="bc.showCalculator" class="calculator-panel">
                                    <div class="calculator-header">公式</div>
                                    <div class="calculator-buttons">
                                      <div class="button-row operator-row">
                                        <el-popover
                                          v-model="bc.showEventPopover"
                                          placement="bottom-start"
                                          width="200"
                                          trigger="click"
                                          popper-class="event-selector-popover"
                                        >
                                          <div class="event-selector-list">
                                            <div
                                              v-for="behavior in behaviorTypes.filter(b => !isRevenueBehaviorOption(b))"
                                              :key="behavior.typeKey"
                                              class="event-selector-item"
                                              @click="
                                                insertEventConfirm(defIndex, groupIndex, bcIndex, behavior.typeKey)
                                              "
                                            >
                                              {{ behavior.label }}
                                            </div>
                                          </div>
                                          <button slot="reference" class="calc-btn insert-event-btn">
                                            <i class="el-icon-plus"></i>
                                            数据指标
                                          </button>
                                        </el-popover>
                                        <button
                                          v-for="operator in ['+', '-', '×', '÷', '(', ')']"
                                          :key="operator"
                                          class="calc-btn operator-btn"
                                          @click="addCalculatorOperator(defIndex, groupIndex, bcIndex, operator)"
                                        >
                                          {{ operator }}
                                        </button>
                                      </div>
                                    </div>
                                    <div
                                      class="calculator-display-container"
                                      @click="handleCalculatorContainerClick($event, defIndex, groupIndex, bcIndex)"
                                    >
                                      <div class="calculator-display-tags">
                                        <template v-for="(token, tokenIndex) in bc.calculatorTokens || []">
                                          <input
                                            v-if="bc.calculatorCursorPosition === tokenIndex"
                                            ref="calculatorInput"
                                            :key="`${defIndex}-${groupIndex}-${bcIndex}-input-${tokenIndex}`"
                                            :data-def-index="defIndex"
                                            :data-group-index="groupIndex"
                                            :data-bc-index="bcIndex"
                                            v-model="bc.calculatorInputValue"
                                            :disabled="isViewMode"
                                            class="calculator-input"
                                            type="text"
                                            :style="{ width: getInputWidth(bc.calculatorInputValue) }"
                                            @input="$forceUpdate()"
                                            @keydown="
                                              handleCalculatorInputKeydown($event, defIndex, groupIndex, bcIndex)
                                            "
                                            @blur="saveCalculatorInputValue(defIndex, groupIndex, bcIndex)"
                                          />
                                          <el-tag
                                            v-if="token.type === 'metric' || token.type === 'operator'"
                                            :key="`${defIndex}-${groupIndex}-${bcIndex}-${tokenIndex}`"
                                            :data-token-index="tokenIndex"
                                            type="info"
                                            closable
                                            @close="removeCalculatorToken(defIndex, groupIndex, bcIndex, tokenIndex)"
                                            class="calculator-tag"
                                          >
                                            {{ token.label }}
                                          </el-tag>
                                          <span
                                            v-else
                                            :key="`${defIndex}-${groupIndex}-${bcIndex}-${tokenIndex}-text`"
                                            :data-token-index="tokenIndex"
                                            class="calculator-text"
                                          >
                                            {{ token.value }}
                                          </span>
                                        </template>
                                        <input
                                          v-if="
                                            !bc.calculatorTokens ||
                                            bc.calculatorTokens.length === 0 ||
                                            bc.calculatorCursorPosition === (bc.calculatorTokens || []).length
                                          "
                                          ref="calculatorInput"
                                          :key="`${defIndex}-${groupIndex}-${bcIndex}-input-end`"
                                          :data-def-index="defIndex"
                                          :data-group-index="groupIndex"
                                          :data-bc-index="bcIndex"
                                          v-model="bc.calculatorInputValue"
                                          :disabled="isViewMode"
                                          class="calculator-input"
                                          type="text"
                                          :style="{ width: getInputWidth(bc.calculatorInputValue) }"
                                          @input="$forceUpdate()"
                                          @keydown="handleCalculatorInputKeydown($event, defIndex, groupIndex, bcIndex)"
                                          @blur="saveCalculatorInputValue(defIndex, groupIndex, bcIndex)"
                                        />
                                      </div>
                                    </div>
                                  </div>

                                  <!-- 子集模式面板 -->
                                  <div v-if="bc.showSubProject" class="subproject-panel">
                                    <div class="condition-content">
                                      <div
                                        class="condition-list-wrapper"
                                        v-if="bc.subConditions && bc.subConditions.length > 0"
                                      >
                                        <div
                                          v-if="bc.subConditions.length >= 2"
                                          class="condition-badge-center"
                                          :class="{ clickable: !isViewMode }"
                                          @click="!isViewMode && toggleSubConditionLogic(defIndex, groupIndex, bcIndex)"
                                        >
                                          {{ bc.subConditionLogicType === 'and' ? '且' : '或' }}
                                        </div>
                                        <div class="condition-list">
                                          <div
                                            v-for="(subItem, subIndex) in bc.subConditions"
                                            :key="'sub-' + subIndex"
                                            class="condition-item"
                                          >
                                            <div class="condition-row">
                                              <div class="condition-selects">
                                                <el-select
                                                  v-model="subItem.behaviorTypeKey"
                                                  placeholder="请选择"
                                                  class="condition-select"
                                                  :disabled="isViewMode"
                                                  @change="onBehaviorTypeChange(eventDef, subItem)"
                                                >
                                                  <el-option
                                                    v-for="b in behaviorTypes"
                                                    :key="b.typeKey"
                                                    :label="b.label"
                                                    :value="b.typeKey"
                                                  />
                                                </el-select>
                                                <template v-if="isIncrementRevenueBehavior(subItem)">
                                                  <span class="special-text">每 &gt;=</span>
                                                  <el-input
                                                    v-model="subItem.value"
                                                    type="number"
                                                    :min="1"
                                                    :step="1"
                                                    :disabled="isViewMode"
                                                    controls-position="right"
                                                    class="revenue-input-number"
                                                  />
                                                  <span class="special-text">美元</span>
                                                  <el-tooltip
                                                    placement="top"
                                                    effect="light"
                                                    popper-class="revenue-rule-tooltip"
                                                  >
                                                    <div slot="content" class="revenue-rule-tooltip-content">
                                                      {{ getRevenueBehaviorTip(subItem) }}
                                                    </div>
                                                    <i class="el-icon-question revenue-rule-icon"></i>
                                                  </el-tooltip>
                                                </template>
                                                <template v-else-if="isAccumulatedRevenueBehavior(subItem)">
                                                  <el-select
                                                    v-model="subItem.operatorValue"
                                                    placeholder="请选择"
                                                    class="condition-select"
                                                    :disabled="isViewMode"
                                                  >
                                                    <el-option
                                                      v-for="op in operators"
                                                      :key="op.value"
                                                      :label="op.label"
                                                      :value="op.value"
                                                    />
                                                  </el-select>
                                                  <el-input
                                                    v-if="subItem.operatorValue !== 'range'"
                                                    v-model="subItem.value"
                                                    placeholder="请输入"
                                                    class="condition-input"
                                                    type="number"
                                                    :disabled="isViewMode"
                                                  />
                                                  <template v-if="subItem.operatorValue === 'range'">
                                                    <el-input
                                                      v-model="subItem.value"
                                                      placeholder="最小值"
                                                      class="condition-input"
                                                      type="number"
                                                      :disabled="isViewMode"
                                                    />
                                                    <span class="range-separator">-</span>
                                                    <el-input
                                                      v-model="subItem.value2"
                                                      placeholder="最大值"
                                                      class="condition-input"
                                                      type="number"
                                                      :disabled="isViewMode"
                                                    />
                                                  </template>
                                                  <span class="special-text">美元</span>
                                                  <el-tooltip placement="top" effect="light" popper-class="revenue-rule-tooltip">
                                                    <div slot="content" class="revenue-rule-tooltip-content">
                                                      {{ getRevenueBehaviorTip(subItem) }}
                                                    </div>
                                                    <i class="el-icon-question revenue-rule-icon"></i>
                                                  </el-tooltip>
                                                </template>
                                                <template v-else>
                                                  <el-select
                                                    v-model="subItem.operatorValue"
                                                    placeholder="请选择"
                                                    class="condition-select"
                                                    :disabled="isViewMode"
                                                  >
                                                    <el-option
                                                      v-for="op in operators"
                                                      :key="op.value"
                                                      :label="op.label"
                                                      :value="op.value"
                                                    />
                                                  </el-select>
                                                  <el-input
                                                    v-if="subItem.operatorValue !== 'range'"
                                                    v-model="subItem.value"
                                                    placeholder="请输入"
                                                    class="condition-input"
                                                    type="number"
                                                    :disabled="isViewMode"
                                                  />
                                                  <span
                                                    v-if="subItem.operatorValue !== 'range' && getBehaviorUnitText(subItem)"
                                                    class="special-text"
                                                  >
                                                    {{ getBehaviorUnitText(subItem) }}
                                                  </span>
                                                  <template v-if="subItem.operatorValue === 'range'">
                                                    <el-input
                                                      v-model="subItem.value"
                                                      placeholder="最小值"
                                                      class="condition-input"
                                                      type="number"
                                                      :disabled="isViewMode"
                                                    />
                                                    <span class="range-separator">-</span>
                                                    <el-input
                                                      v-model="subItem.value2"
                                                      placeholder="最大值"
                                                      class="condition-input"
                                                      type="number"
                                                      :disabled="isViewMode"
                                                    />
                                                    <span v-if="getBehaviorUnitText(subItem)" class="special-text">
                                                      {{ getBehaviorUnitText(subItem) }}
                                                    </span>
                                                  </template>
                                                </template>
                                              </div>
                                              <el-button
                                                v-if="!isViewMode"
                                                type="text"
                                                class="delete-btn"
                                                @click="deleteSubCondition(defIndex, groupIndex, bcIndex, subIndex)"
                                              >
                                                <i class="el-icon-error"></i>
                                              </el-button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <el-button
                                        v-if="!isViewMode"
                                        size="small"
                                        plain
                                        class="add-btn"
                                        @click="addSubCondition(defIndex, groupIndex, bcIndex)"
                                      >
                                        <i class="el-icon-plus"></i>
                                        添加
                                      </el-button>
                                    </div>
                                  </div>

                                  <!-- 从属筛选条件区域 -->
                                  <div v-if="bc.subFilters && bc.subFilters.length > 0" class="subfilter-area">
                                    <div
                                      v-for="(sf, sfIndex) in bc.subFilters"
                                      :key="'sf-' + sfIndex"
                                      class="subfilter-row"
                                    >
                                      <div class="subfilter-connector">
                                        <span
                                          class="sf-vline"
                                          :class="{ 'sf-vline-full': sfIndex < bc.subFilters.length - 1 }"
                                        ></span>
                                        <span class="sf-hline"></span>
                                      </div>
                                      <div class="subfilter-fields">
                                        <el-select
                                          v-model="sf.field"
                                          class="condition-select"
                                          :disabled="isViewMode"
                                          size="small"
                                          @change="onSubFilterFieldChange(defIndex, groupIndex, bcIndex, sfIndex)"
                                        >
                                          <el-option
                                            v-for="opt in getAvailableSubFilterFields(
                                              defIndex,
                                              groupIndex,
                                              bcIndex,
                                              sfIndex,
                                            )"
                                            :key="opt.value"
                                            :label="opt.label"
                                            :value="opt.value"
                                          />
                                        </el-select>
                                        <el-select
                                          v-model="sf.operator"
                                          class="condition-select"
                                          :disabled="isViewMode"
                                          size="small"
                                        >
                                          <el-option
                                            v-for="opt in subFilterOps"
                                            :key="opt.value"
                                            :label="opt.label"
                                            :value="opt.value"
                                          />
                                        </el-select>
                                        <el-select
                                          v-model="sf.value"
                                          placeholder="请选择"
                                          class="condition-select"
                                          :disabled="isViewMode"
                                          size="small"
                                          multiple
                                          collapse-tags
                                          filterable
                                        >
                                          <el-option
                                            v-for="opt in getSubFilterValueOpts(sf.field)"
                                            :key="opt.value"
                                            :label="opt.label"
                                            :value="opt.value"
                                          />
                                        </el-select>
                                        <el-button
                                          v-if="!isViewMode"
                                          type="text"
                                          class="delete-btn"
                                          @click="deleteSubFilter(defIndex, groupIndex, bcIndex, sfIndex)"
                                        >
                                          <i class="el-icon-error"></i>
                                        </el-button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <el-button
                            v-if="!isViewMode"
                            size="small"
                            plain
                            class="add-btn"
                            @click="addConditionToGroup(defIndex, groupIndex)"
                          >
                            <i class="el-icon-plus"></i>
                            添加条件
                          </el-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <el-button v-if="!isViewMode" size="small" plain class="add-btn" @click="addBehaviorGroup(defIndex)">
                  <i class="el-icon-plus"></i>
                  添加条件组
                </el-button>
              </div>
            </div>
          </div>

          <!-- 放大策略 -->
          <div class="amplify-strategy-section">
            <div class="definition-block amplify-block">
              <div class="amplify-block-head">
                <h3 class="definition-subtitle">放大策略</h3>
                <el-switch
                  v-model="eventDef.amplify.enabled"
                  :disabled="isViewMode"
                  active-color="#1890ff"
                  @change="onAmplifyEnabledChange(eventDef)"
                />
              </div>

              <div v-if="eventDef.amplify.enabled" class="amplify-config">
                <div class="amplify-config-left">
                  <div class="amplify-row amplify-row--stack">
                    <span class="amplify-field-label">放大类型</span>
                    <el-radio-group
                      v-model="eventDef.amplify.dimension"
                      :disabled="isViewMode"
                      @change="onAmplifyDimensionChange(eventDef)"
                    >
                      <el-radio label="login_count">
                        {{ getAmplifyDimensionLabel(eventDef, 'login_count') }}
                        <el-tooltip placement="bottom-start" effect="light" popper-class="amplify-login-rule-tooltip">
                          <div slot="content" class="amplify-login-rule-tooltip-content">
                            <div class="amplify-login-rule-tooltip-title">计算规则</div>
                            <div>1.首次启动固定计 1 次。</div>
                            <div>2.此后每次启动与上次退出的间隔 &gt;= 10 分钟 时，加 1 次。</div>
                          </div>
                          <i class="el-icon-question amplify-rule-icon"></i>
                        </el-tooltip>
                      </el-radio>
                      <el-radio label="ad_revenue">{{ getAmplifyDimensionLabel(eventDef, 'ad_revenue') }}</el-radio>
                    </el-radio-group>
                  </div>

                  <div class="amplify-row amplify-row--stack">
                    <span class="amplify-field-label">放大对象</span>
                    <el-radio-group
                      v-model="eventDef.amplify.target"
                      class="amplify-target-radio-group"
                      :disabled="isViewMode"
                      @change="onAmplifyTargetChange(eventDef)"
                    >
                      <el-radio label="count">上报次数*N(默认)</el-radio>
                      <el-radio label="value" :disabled="isAmplifyValueTargetDisabled(eventDef)">上报值*N</el-radio>
                    </el-radio-group>
                  </div>
                </div>

                <div class="amplify-config-right">
                  <div class="amplify-table-toolbar">
                    <el-button size="mini" plain :disabled="isViewMode" @click="openAmplifyImportDialog(eventDef)">
                      导入配置
                    </el-button>
                    <el-select
                      v-if="eventDef.amplify.defaultLevels.length > 1"
                      :value="''"
                      size="mini"
                      class="amplify-level-jump-select"
                      placeholder="跳转层级"
                      @change="levelIndex => jumpToAmplifyLevel(defIndex, levelIndex)"
                    >
                      <el-option
                        v-for="(level, levelIndex) in eventDef.amplify.defaultLevels"
                        :key="'amplify-level-jump-' + levelIndex"
                        :label="'层级' + (levelIndex + 1)"
                        :value="levelIndex"
                      />
                    </el-select>
                  </div>

                  <div class="amplify-table-scroll" :data-amplify-table-index="defIndex">
                    <table class="amplify-table">
                      <thead>
                        <tr>
                          <th rowspan="2">国家地区</th>
                          <th
                            v-for="(level, levelIndex) in eventDef.amplify.defaultLevels"
                            :key="'amplify-level-head-' + levelIndex"
                            :data-amplify-level-index="levelIndex"
                            colspan="2"
                          >
                            <span>层级{{ levelIndex + 1 }}</span>
                            <button
                              v-if="!isViewMode && eventDef.amplify.defaultLevels.length > 1"
                              type="button"
                              class="amplify-level-remove-btn"
                              @click="deleteAmplifyLevel(eventDef, levelIndex)"
                            >
                              ×
                            </button>
                          </th>
                          <th rowspan="2" class="amplify-add-level-cell">
                            <button
                              v-if="!isViewMode"
                              type="button"
                              class="amplify-table-action"
                              @click="addAmplifyLevel(eventDef)"
                            >
                              添加层级
                            </button>
                          </th>
                        </tr>
                        <tr>
                          <template v-for="(level, levelIndex) in eventDef.amplify.defaultLevels">
                            <th :key="'amplify-threshold-head-' + levelIndex">
                              {{ getAmplifyThresholdLabel(eventDef) }}
                            </th>
                            <th :key="'amplify-multiplier-head-' + levelIndex">
                              {{ getAmplifyMultiplierLabel(eventDef) }}
                            </th>
                          </template>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in getAmplifyTableRows(eventDef)"
                          :key="'amplify-row-' + row.kind + '-' + row.countryIndex"
                          class="amplify-config-row"
                        >
                          <td class="amplify-row-label-cell">
                            <span v-if="row.kind === 'default'">default</span>
                            <div v-else class="amplify-row-label-inner">
                              <el-select
                                :value="eventDef.amplify.countryLevels[row.countryIndex].country"
                                :disabled="isViewMode"
                                size="mini"
                                filterable
                                placeholder="选择"
                                class="amplify-country-select"
                                @change="value => onAmplifyCountryChange(eventDef, row.countryIndex, value)"
                              >
                                <el-option
                                  v-for="country in getAvailableAmplifyCountries(eventDef, row.countryIndex)"
                                  :key="country.value"
                                  :label="country.value"
                                  :value="country.value"
                                />
                              </el-select>
                              <button
                                v-if="!isViewMode"
                                type="button"
                                class="amplify-country-remove-btn"
                                @click="deleteAmplifyCountry(eventDef, row.countryIndex)"
                              >
                                ×
                              </button>
                            </div>
                          </td>
                          <template v-for="(level, levelIndex) in eventDef.amplify.defaultLevels">
                            <td :key="'amplify-threshold-' + row.kind + '-' + row.countryIndex + '-' + levelIndex">
                              <el-input
                                v-if="row.levels[levelIndex]"
                                v-model="row.levels[levelIndex].threshold"
                                :disabled="isViewMode"
                                class="amplify-table-input"
                                size="mini"
                                @input="value => onAmplifyThresholdInput(row.levels[levelIndex], value, eventDef)"
                              />
                            </td>
                            <td :key="'amplify-multiplier-' + row.kind + '-' + row.countryIndex + '-' + levelIndex">
                              <el-input
                                v-if="row.levels[levelIndex]"
                                v-model="row.levels[levelIndex].multiplier"
                                :disabled="isViewMode"
                                class="amplify-table-input"
                                size="mini"
                                @input="value => onAmplifyMultiplierInput(row.levels[levelIndex], value, eventDef)"
                              />
                            </td>
                          </template>
                          <td class="amplify-add-level-body-cell"></td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td :colspan="getAmplifyFooterColspan(eventDef)">
                            <button
                              v-if="!isViewMode"
                              type="button"
                              class="amplify-table-action"
                              @click="addAmplifyCountry(eventDef)"
                            >
                              添加国家地区
                            </button>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <el-dialog
            title="导入配置"
            :visible.sync="eventDef.amplify.importDialogVisible"
            width="520px"
            custom-class="amplify-import-dialog"
            append-to-body
            :close-on-click-modal="false"
          >
            <el-upload
              drag
              class="amplify-import-upload"
              action="#"
              :auto-upload="false"
              :show-file-list="false"
              accept=".xlsx,.xls,.csv"
              :on-change="file => handleAmplifyImportFileChange(eventDef, file)"
            >
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">
                {{ eventDef.amplify.importFileName || '点击选择或拖入文件' }}
              </div>
            </el-upload>
            <div class="amplify-import-tip">支持 .xlsx / .xls / .csv，提交后覆盖当前配置。</div>
            <p v-if="eventDef.amplify.importError" class="amplify-import-error">
              {{ eventDef.amplify.importError }}
            </p>
            <span slot="footer">
              <el-button @click="closeAmplifyImportDialog(eventDef)">取消</el-button>
              <el-button type="primary" @click="submitAmplifyImport(eventDef)">提交导入</el-button>
            </span>
          </el-dialog>
        </div>
      </div>

      <!-- 复制事件 / 添加事件（仅新增模式） -->
      <div v-if="mode === 'add'" class="event-def-actions">
        <el-button type="text" class="action-link-btn" @click="copyLastEvent">复制事件</el-button>
        <el-button type="text" class="action-link-btn" @click="addEventDef">
          <i class="el-icon-circle-plus-outline"></i>
          添加事件
        </el-button>
      </div>
    </div>

    <!-- 底部按钮 -->
    <div class="footer-btns">
      <el-button @click="handleGoBack">{{ isViewMode ? '返回列表' : '取消' }}</el-button>
      <div class="footer-right">
        <el-button v-if="!isViewMode" type="primary" @click="handleSubmit" :loading="saveLoading">
          {{ mode === 'edit' ? '保存配置' : '提交' }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import XLSX from 'xlsx'
import SvgIcon from '@/components/SvgIcon.vue'
import {
  getBuyEventEnumAll,
  getBuyEventMergedDetail,
  createBuyEventMerged,
  updateBuyEventMerged,
  getGameModeIntersect,
  getAdzCodeIntersect,
} from '@/api/eventManager'
import { getCountryListFetch, getmediaSourceFetch } from '@/api/api'

const INCREMENT_REVENUE_BEHAVIOR_CODES = ['ad_usd_revenue', 'pay_purchase_usd_revenue']
const ACCUMULATED_REVENUE_BEHAVIOR_CODES = ['cumulative_ad_usd_revenue', 'cumulative_pay_purchase_usd_revenue']
const REVENUE_BEHAVIOR_CODES = [...INCREMENT_REVENUE_BEHAVIOR_CODES, ...ACCUMULATED_REVENUE_BEHAVIOR_CODES]
const VALUED_BEHAVIOR_CODES = [...INCREMENT_REVENUE_BEHAVIOR_CODES, 'valid_login_interval']
const LEVEL_RELATED_BEHAVIOR_CODES = ['level_start_cnt', 'level_complete', 'level_fail_cnt', 'level_revive_cnt']
const AD_TYPE_FILTER_BEHAVIOR_CODES = ['ad_ecpm', 'daily_ad_ecpm']
const MINUTE_UNIT_BEHAVIOR_CODES = ['app_run_duration', 'valid_login_interval']
const DOLLAR_UNIT_BEHAVIOR_CODES = ['ad_ecpm', 'daily_ad_ecpm', ...REVENUE_BEHAVIOR_CODES]
const EVENT_NAME_PATTERN = /^[A-Za-z0-9_]+$/
const EVENT_NAME_MAX_LENGTH = 40

function decodeHtmlEntities(value) {
  if (value === undefined || value === null) return ''
  const entityMap = {
    '&gt;': '>',
    '&lt;': '<',
    '&ge;': '>=',
    '&le;': '<=',
    '&ne;': '!=',
    '&eq;': '=',
    '&amp;': '&',
  }
  return String(value).replace(/&(gt|lt|ge|le|ne|eq|amp);/g, match => entityMap[match] || match)
}

function createEmptyBehaviorCondition(logicType) {
  return {
    timeDimensionValue: '',
    windowType: 'unlimited',
    windowDays: '',
    behaviorTypeKey: '',
    behaviorTypeValue: '',
    behaviorTypeLabel: '',
    operatorValue: '',
    value: '',
    value2: '',
    countryThresholds: [],
    showCountryThresholds: false,
    logicType: logicType || 'and',
    showCalculator: false,
    showCalculatorText: '',
    calculatorTokens: [],
    calculatorInputValue: '',
    calculatorCursorPosition: 0,
    showSubProject: false,
    showEventPopover: false,
    subConditions: [],
    subConditionLogicType: 'and',
    subFilters: [],
  }
}

function createEmptyBehaviorGroup() {
  return {
    conditions: [],
    logicType: 'and',
  }
}

function createAmplifyLevel(level) {
  return {
    threshold: level && level.threshold !== undefined && level.threshold !== null ? String(level.threshold) : '',
    multiplier: level && level.multiplier !== undefined && level.multiplier !== null ? String(level.multiplier) : '',
  }
}

function createEmptyAmplifyConfig() {
  return {
    enabled: false,
    target: 'count',
    dimension: 'login_count',
    defaultLevels: [createAmplifyLevel()],
    countryLevels: [],
    importDialogVisible: false,
    importFile: null,
    importFileName: '',
    importError: '',
  }
}

function createEmptyEventDef() {
  return {
    name: '',
    remark: '',
    reportPlatforms: [],
    reportWindowEnabled: false,
    reportWindowHours: 24,
    collapsed: false,
    userAttributes: [],
    userLogicType: 'and',
    behaviorGroups: [],
    behaviorGroupLogicType: 'and',
    amplify: createEmptyAmplifyConfig(),
  }
}

export default {
  name: 'EventConfig',
  components: { SvgIcon },

  beforeRouteLeave(to, from, next) {
    if (this.isViewMode || this.submitted) {
      next()
      return
    }
    this.$confirm('确定要离开吗？未保存的内容将丢失。', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    })
      .then(() => next())
      .catch(() => next(false))
  },

  data() {
    return {
      pageTitle: '新增事件',
      mode: 'add',
      copySourceId: '',
      copyTargetProductId: '',

      formData: {
        products: [],
      },
      productList: [],

      eventDefinitions: [],
      reportPlatformOptions: [
        { label: 'Firebase', value: 'FIREBASE' },
        { label: 'AppsFlyer', value: 'AF' },
        { label: 'Facebook', value: 'FACEBOOK' },
      ],

      // 从属筛选选项
      subFilterFields: [
        { value: 'play_name', label: '玩法名称' },
        { value: 'mode_name', label: '模式名称' },
      ],
      subFilterOps: [
        { value: 'eq', label: '等于' },
        { value: 'neq', label: '不等于' },
      ],
      subFilterPlayNameOpts: [],
      subFilterModeNameOpts: [],
      subFilterAdTypeOpts: [],
      windowTypeOptions: [],
      windowDayOptions: Array.from({ length: 30 }, (_, index) => ({ value: String(index + 1) })),

      // 枚举数据
      userAttributeTypes: [],
      timeDimensions: [],
      behaviorTypes: [],
      operators: [],
      countryList: [],
      mediaSourceList: [],

      saveLoading: false,
      enumLoading: false,
      submitted: false,

      status: 1,
    }
  },

  watch: {
    'formData.products': {
      handler() {
        this.fetchGameModeIntersect()
        this.fetchAdzCodeIntersect()
      },
      deep: true,
      immediate: true,
    },
  },

  computed: {
    isViewMode() {
      return this.mode === 'view'
    },
    isAllProductsSelected() {
      return this.productList.length > 0 && this.formData.products.length === this.productList.length
    },
    isProductsIndeterminate() {
      return this.formData.products.length > 0 && this.formData.products.length < this.productList.length
    },
  },

  async mounted() {
    await this.loadEnumData()

    this.mode = this.$route.query.mode || 'add'
    const id = this.$route.query.id
    this.copySourceId = this.$route.query.copySourceId || ''
    this.copyTargetProductId = this.normalizeProductId(this.$route.query.targetProductId || '')

    if (this.mode === 'copy' && id) {
      this.mode = 'add'
      this.copySourceId = id
      this.status = 0
    }

    if (this.mode === 'add') {
      if (this.copySourceId) {
        this.pageTitle = '新增事件'
        this.status = 0
        await this.loadEventData(this.copySourceId)
        if (this.copyTargetProductId) {
          this.formData.products = [this.copyTargetProductId]
        }
      } else {
        this.pageTitle = '新增事件'
        this.status = 1
        this.eventDefinitions = [createEmptyEventDef()]
        this.eventDefinitions.forEach(eventDef => this.syncReportPlatformsByBehavior(eventDef, true))
      }
    } else if (this.mode === 'edit' && id) {
      this.pageTitle = '编辑事件'
      await this.loadEventData(id)
    } else if (this.mode === 'view' && id) {
      this.pageTitle = '查看事件'
      await this.loadEventData(id)
    }
  },

  methods: {
    normalizeProductId(productId) {
      if (productId === undefined || productId === null || productId === '') return ''
      const matched = (this.productList || []).find(item => String(item.afid) === String(productId))
      return matched ? matched.afid : productId
    },

    buildBehaviorTypeKey(item) {
      return item.code || ''
    },

    getBehaviorByKey(typeKey) {
      return this.behaviorTypes.find(item => item.typeKey === typeKey)
    },

    resolveBehaviorTypeKey(typeValue, typeLabel) {
      if (!typeValue) return ''
      if (typeLabel) {
        const matchedByLabel = this.behaviorTypes.find(item => item.value === typeValue && item.label === typeLabel)
        if (matchedByLabel) return matchedByLabel.typeKey
      }
      const matched = this.behaviorTypes.find(item => item.value === typeValue)
      return matched ? matched.typeKey : typeValue
    },

    getBehaviorLabel(condition) {
      if (!condition) return ''
      if (condition.behaviorTypeLabel) return condition.behaviorTypeLabel
      const behavior = this.getBehaviorByKey(condition.behaviorTypeKey)
      return behavior ? behavior.label : ''
    },

    getBehaviorUnitText(condition) {
      if (!condition) return ''
      if (MINUTE_UNIT_BEHAVIOR_CODES.includes(condition.behaviorTypeValue)) return '分钟'
      if (DOLLAR_UNIT_BEHAVIOR_CODES.includes(condition.behaviorTypeValue)) return '美元'
      return ''
    },

    isRevenueBehaviorOption(behavior) {
      return REVENUE_BEHAVIOR_CODES.includes(behavior.value)
    },

    isRevenueBehavior(condition) {
      return REVENUE_BEHAVIOR_CODES.includes(condition.behaviorTypeValue)
    },

    isAccumulatedRevenueBehavior(condition) {
      return ACCUMULATED_REVENUE_BEHAVIOR_CODES.includes(condition.behaviorTypeValue)
    },

    isAdRevenueBehavior(condition) {
      return condition.behaviorTypeValue === 'cumulative_ad_usd_revenue'
    },

    isPayRevenueBehavior(condition) {
      return condition.behaviorTypeValue === 'cumulative_pay_purchase_usd_revenue'
    },

    isIncrementRevenueBehavior(condition) {
      return INCREMENT_REVENUE_BEHAVIOR_CODES.includes(condition.behaviorTypeValue)
    },

    getRevenueBehaviorTip(condition) {
      const tips = {
        ad_usd_revenue:
          '规则概要 本指标为带值行为条件。系统按每位用户分别统计广告增量流水：新产生的广告收入按时间先后入账本。您填写的美元数表示每积满多少美元触发一次。每次触发须将您配置的门槛作为该次事件参数中的 value 上报。\n\n某次加完后，若金额还不足以跨过一整档门槛，剩余部分会留在账上，和下一笔广告收入接着算。若单笔收入特别大，也可能一次跨过多个门槛，从而连续触发多次上报，每次数值仍为您设的门槛。\n\n举例（假设门槛填 1 美元；按先后顺序来了四笔广告收入：2.0、1.8、1.4、0.7 美元）\n• 第 1 笔 2.0：相当于积满 2 个 1 美元 → 触发 2 次，每次上报数值均为 1。\n• 第 2 笔 1.8：再积满 1 个 1 美元，还剩 0.8 美元在账上 → 触发 1 次，数值为 1。\n• 第 3 笔 1.4：与上次余数 0.8 相加得 2.2，积满 2 个 1 美元，还剩 0.2 → 触发 2 次，每次数值均为 1。\n• 第 4 笔 0.7：与余数 0.2 相加仅 0.9，不足 1 美元 → 暂不触发。\n\n若您填的是其他正数（例如 5 美元），把例子里的「1 美元」换成您填的门槛即可，算法相同。',
        pay_purchase_usd_revenue:
          '规则概要 本指标为带值行为条件。系统按每位用户分别统计内购增量流水：新产生的内购收入按时间先后入账本。您填写的美元数表示每积满多少美元触发一次。每次触发须将您配置的门槛作为该次事件参数中的 value 上报。\n\n某次加完后，若金额还不足以跨过一整档门槛，剩余部分会留在账上，和下一笔内购收入接着算。若单笔金额特别大，也可能一次跨过多个门槛，从而连续触发多次上报，每次数值仍为您设的门槛。\n\n举例（假设门槛填 1 美元；按先后顺序来了四笔内购收入：2.0、1.8、1.4、0.7 美元）\n• 第 1 笔 2.0：相当于积满 2 个 1 美元 → 触发 2 次，每次上报数值均为 1。\n• 第 2 笔 1.8：再积满 1 个 1 美元，还剩 0.8 美元在账上 → 触发 1 次，数值为 1。\n• 第 3 笔 1.4：与上次余数 0.8 相加得 2.2，积满 2 个 1 美元，还剩 0.2 → 触发 2 次，每次数值均为 1。\n• 第 4 笔 0.7：与余数 0.2 相加仅 0.9，不足 1 美元 → 暂不触发。\n\n若您填的是其他正数（例如 5 美元），把例子里的「1 美元」换成您填的门槛即可，算法相同。',
        cumulative_ad_usd_revenue:
          '规则概要 系统按每位用户分别统计其在统计口径下的累计广告收入（全部广告收入金额相加）。当累计值满足您通过运算符与门槛配置的条件时触发服务端事件上报。',
        cumulative_pay_purchase_usd_revenue:
          '规则概要 系统按每位用户分别统计其在统计口径下的累计内购收入（全部内购金额相加）。当累计值满足您配置的条件时触发服务端事件上报。',
      }
      return tips[condition.behaviorTypeValue] || ''
    },

    isValuedBehavior(condition) {
      return condition && this.isValuedBehaviorValue(condition.behaviorTypeValue)
    },

    isValuedBehaviorValue(behaviorValue) {
      return !!behaviorValue && VALUED_BEHAVIOR_CODES.includes(behaviorValue)
    },

    getCalculatorMetricValues(condition) {
      if (!condition) return []
      const values = []
      ;(condition.calculatorTokens || []).forEach(token => {
        if (token && token.type === 'metric' && token.value) values.push(token.value)
      })
      String(condition.showCalculatorText || '').replace(/\{([^}]+)\}/g, (match, metricText) => {
        const metric = String(metricText || '').trim()
        const behavior = this.behaviorTypes.find(item => item.label === metric || item.value === metric)
        if (behavior && behavior.value) values.push(behavior.value)
        return match
      })
      return [...new Set(values)]
    },

    hasValuedBehavior(eventDef) {
      const groups = eventDef.behaviorGroups || []
      return groups.some(group =>
        (group.conditions || []).some(condition => {
          const hasMainValuedBehavior = this.isValuedBehavior(condition)
          const hasCalculatorValuedBehavior = this.getCalculatorMetricValues(condition).some(value =>
            this.isValuedBehaviorValue(value),
          )
          const hasSubValuedBehavior = (condition.subConditions || []).some(subItem => this.isValuedBehavior(subItem))
          return hasMainValuedBehavior || hasCalculatorValuedBehavior || hasSubValuedBehavior
        }),
      )
    },

    isAmplifyValueTargetDisabled(eventDef) {
      return !this.hasValuedBehavior(eventDef)
    },

    syncAmplifyTargetByBehavior(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      if (this.isAmplifyValueTargetDisabled(eventDef) && amplify.target === 'value') {
        this.$set(amplify, 'target', 'count')
        this.normalizeAmplifyIntegerValues(eventDef)
      }
    },

    hasBehaviorValue(eventDef, behaviorValue) {
      const groups = eventDef.behaviorGroups || []
      return groups.some(group =>
        (group.conditions || []).some(condition => {
          const hasMainBehavior = condition.behaviorTypeValue === behaviorValue
          const hasSubBehavior = (condition.subConditions || []).some(
            subItem => subItem.behaviorTypeValue === behaviorValue,
          )
          return hasMainBehavior || hasSubBehavior
        }),
      )
    },

    isReportPlatformDisabled(eventDef, platform) {
      return this.isAppsFlyerPlatform(platform) && this.hasValuedBehavior(eventDef)
    },

    getAllReportPlatformValues() {
      return (this.reportPlatformOptions || []).map(item => item.value).filter(Boolean)
    },

    syncReportPlatformsByBehavior(eventDef, initialize = false) {
      const currentPlatforms = Array.isArray(eventDef.reportPlatforms) ? eventDef.reportPlatforms : []
      let nextPlatforms = initialize && currentPlatforms.length === 0 ? this.getAllReportPlatformValues() : currentPlatforms
      if (this.hasValuedBehavior(eventDef)) {
        nextPlatforms = nextPlatforms.filter(platform => !this.isAppsFlyerPlatform(platform))
      }
      if (!this.isSamePlatformList(eventDef.reportPlatforms, nextPlatforms)) {
        this.$set(eventDef, 'reportPlatforms', nextPlatforms)
      }
    },

    isAppsFlyerPlatform(platform) {
      return ['AF', 'AppsFlyer', 'appsflyer'].includes(platform)
    },

    isSamePlatformList(currentPlatforms, nextPlatforms) {
      const current = Array.isArray(currentPlatforms) ? currentPlatforms : []
      if (current.length !== nextPlatforms.length) return false
      return current.every((platform, index) => platform === nextPlatforms[index])
    },

    onBehaviorTypeChange(eventDef, condition) {
      const behavior = this.getBehaviorByKey(condition.behaviorTypeKey)
      if (!behavior) return
      this.$set(condition, 'behaviorTypeValue', behavior.value)
      this.$set(condition, 'behaviorTypeLabel', behavior.label)
      if (this.isIncrementRevenueBehavior(condition)) {
        this.$set(condition, 'operatorValue', '')
        this.$set(condition, 'value2', '')
      }
      if (condition.subFilters) {
        const availableFields = this.getSubFilterFieldsForBehavior(condition).map(item => item.value)
        this.$set(
          condition,
          'subFilters',
          condition.subFilters.filter(sf => availableFields.includes(sf.field)),
        )
      }
      this.syncReportPlatformsByBehavior(eventDef)
      this.syncAmplifyDimensionByBehavior(eventDef)
      this.syncAmplifyTargetByBehavior(eventDef)
    },

    deriveAmplifyDimension(eventDef) {
      if (
        this.hasBehaviorValue(eventDef, 'ad_usd_revenue') ||
        this.hasBehaviorValue(eventDef, 'increment_ad_usd_revenue')
      ) {
        return 'ad_revenue'
      }
      if (this.hasBehaviorValue(eventDef, 'valid_login_interval')) return 'login_count'
      return 'login_count'
    },

    ensureAmplifyConfig(eventDef) {
      if (!eventDef.amplify) {
        this.$set(eventDef, 'amplify', createEmptyAmplifyConfig())
      }
      const amplify = eventDef.amplify
      if (!Array.isArray(amplify.defaultLevels) || amplify.defaultLevels.length === 0) {
        this.$set(amplify, 'defaultLevels', [createAmplifyLevel()])
      }
      if (!Array.isArray(amplify.countryLevels)) {
        this.$set(amplify, 'countryLevels', [])
      }
      if (!amplify.target) this.$set(amplify, 'target', 'count')
      if (!amplify.dimension) this.$set(amplify, 'dimension', 'login_count')
      return amplify
    },

    onAmplifyEnabledChange(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      if (amplify.enabled) {
        this.syncAmplifyDimensionByBehavior(eventDef, true)
        this.syncAmplifyTargetByBehavior(eventDef)
      }
    },

    syncAmplifyDimensionByBehavior(eventDef, force = false) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      if (!amplify.enabled) return
      const dimension = this.deriveAmplifyDimension(eventDef)
      if (force || amplify.dimension !== dimension) {
        this.$set(amplify, 'dimension', dimension)
        this.normalizeAmplifyIntegerValues(eventDef)
      }
    },

    onAmplifyDimensionChange(eventDef) {
      this.ensureAmplifyConfig(eventDef)
      this.normalizeAmplifyIntegerValues(eventDef)
    },

    onAmplifyTargetChange(eventDef) {
      this.syncAmplifyTargetByBehavior(eventDef)
      this.normalizeAmplifyIntegerValues(eventDef)
    },

    getAmplifyDimensionLabel(eventDef, dimension) {
      const labelMap = {
        login_count: '按累计有效登录次数',
        ad_revenue: '按累计广告收入',
      }
      if (!eventDef.amplify || !eventDef.amplify.enabled) return labelMap[dimension]
      return `${labelMap[dimension]}${this.deriveAmplifyDimension(eventDef) === dimension ? '(默认)' : ''}`
    },

    getAmplifyThresholdLabel(eventDef) {
      return eventDef.amplify && eventDef.amplify.dimension === 'ad_revenue' ? '累计广告收入' : '累计有效登录次数'
    },

    getAmplifyMultiplierLabel(eventDef) {
      return eventDef.amplify && eventDef.amplify.target === 'value' ? '上报值*N' : '上报次数*N'
    },

    getAmplifyTableRows(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      return [
        {
          kind: 'default',
          country: 'default',
          countryIndex: -1,
          levels: amplify.defaultLevels,
        },
        ...amplify.countryLevels.map((item, index) => ({
          kind: 'country',
          country: item.country,
          countryIndex: index,
          levels: item.levels,
        })),
      ]
    },

    onAmplifyThresholdInput(level, value, eventDef) {
      const text = String(value || '')
      if (eventDef.amplify.dimension === 'login_count') {
        this.$set(level, 'threshold', text.replace(/\D/g, ''))
        return
      }
      this.$set(level, 'threshold', this.toDecimalText(text, 4))
    },

    onAmplifyMultiplierInput(level, value, eventDef) {
      const text = String(value || '')
      if (eventDef.amplify.target === 'count') {
        this.$set(level, 'multiplier', text.replace(/\D/g, ''))
        return
      }
      this.$set(level, 'multiplier', this.toDecimalText(text, 4))
    },

    normalizeAmplifyIntegerValues(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      const allLevels = [
        ...amplify.defaultLevels,
        ...amplify.countryLevels.reduce((levels, countryConfig) => levels.concat(countryConfig.levels || []), []),
      ]
      allLevels.forEach(level => {
        if (amplify.dimension === 'login_count') {
          this.$set(level, 'threshold', this.toPositiveIntegerText(level.threshold))
        } else {
          this.$set(level, 'threshold', this.toDecimalText(level.threshold, 4))
        }
        if (amplify.target === 'count') {
          this.$set(level, 'multiplier', this.toPositiveIntegerText(level.multiplier))
        } else {
          this.$set(level, 'multiplier', this.toDecimalText(level.multiplier, 4))
        }
      })
    },

    toDecimalText(value, decimalPlaces) {
      const text = String(value === undefined || value === null ? '' : value)
      const normalized = text.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
      const decimalIndex = normalized.indexOf('.')
      if (decimalIndex < 0) return normalized
      return `${normalized.slice(0, decimalIndex + 1)}${normalized.slice(decimalIndex + 1, decimalIndex + 1 + decimalPlaces)}`
    },

    toPositiveIntegerText(value) {
      const text = String(value === undefined || value === null ? '' : value).trim()
      if (!text) return ''
      const number = Number(text)
      if (!Number.isFinite(number) || number <= 0) return ''
      return String(Math.max(1, Math.trunc(number)))
    },

    getAmplifyFooterColspan(eventDef) {
      return 2 + eventDef.amplify.defaultLevels.length * 2
    },

    normalizeAmplifyLevels(levels, levelCount) {
      const normalized = Array.isArray(levels) ? levels.map(level => createAmplifyLevel(level)) : []
      const count = Math.max(1, levelCount || normalized.length || 1)
      while (normalized.length < count) normalized.push(createAmplifyLevel())
      return normalized
    },

    addAmplifyLevel(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      amplify.defaultLevels.push(createAmplifyLevel())
      amplify.countryLevels.forEach(countryConfig => {
        countryConfig.levels.push(createAmplifyLevel())
      })
    },

    jumpToAmplifyLevel(defIndex, levelIndex) {
      this.$nextTick(() => {
        const scrollEl = this.$el.querySelector(`[data-amplify-table-index="${defIndex}"]`)
        const target = scrollEl && scrollEl.querySelector(`[data-amplify-level-index="${levelIndex}"]`)
        if (!scrollEl || !target) return
        const left = Math.max(0, target.offsetLeft - 104)
        if (typeof scrollEl.scrollTo === 'function') {
          scrollEl.scrollTo({ left, behavior: 'smooth' })
        } else {
          scrollEl.scrollLeft = left
        }
      })
    },

    deleteAmplifyLevel(eventDef, levelIndex) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      if (amplify.defaultLevels.length <= 1) return
      amplify.defaultLevels.splice(levelIndex, 1)
      amplify.countryLevels.forEach(countryConfig => {
        countryConfig.levels.splice(levelIndex, 1)
        if (countryConfig.levels.length === 0) countryConfig.levels.push(createAmplifyLevel())
      })
    },

    addAmplifyCountry(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      const available = this.getAvailableAmplifyCountries(eventDef, -1)
      if (available.length === 0) {
        this.$message.warning('已添加全部可选国家地区')
        return
      }
      amplify.countryLevels.push({
        country: '',
        levels: this.normalizeAmplifyLevels([], amplify.defaultLevels.length),
      })
    },

    deleteAmplifyCountry(eventDef, countryIndex) {
      eventDef.amplify.countryLevels.splice(countryIndex, 1)
    },

    getAvailableAmplifyCountries(eventDef, currentIndex) {
      const selected = (eventDef.amplify.countryLevels || [])
        .map((item, index) => (index !== currentIndex ? item.country : null))
        .filter(Boolean)
      return this.countryList.filter(item => item.value && !selected.includes(item.value))
    },

    onAmplifyCountryChange(eventDef, countryIndex, value) {
      const countryConfig = eventDef.amplify.countryLevels[countryIndex]
      if (!countryConfig) return
      this.$set(countryConfig, 'country', value)
    },

    openAmplifyImportDialog(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      this.$set(amplify, 'importDialogVisible', true)
      this.$set(amplify, 'importError', '')
    },

    closeAmplifyImportDialog(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      this.$set(amplify, 'importDialogVisible', false)
      this.$set(amplify, 'importFile', null)
      this.$set(amplify, 'importFileName', '')
      this.$set(amplify, 'importError', '')
    },

    handleAmplifyImportFileChange(eventDef, file) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      this.$set(amplify, 'importFile', file && file.raw ? file.raw : null)
      this.$set(amplify, 'importFileName', file && file.name ? file.name : '')
      this.$set(amplify, 'importError', '')
    },

    async submitAmplifyImport(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      if (!amplify.importFile) {
        this.$set(amplify, 'importError', '请先选择要导入的表格')
        return
      }
      try {
        const importedConfig = await this.parseAmplifyImportFile(amplify.importFile)
        if (importedConfig.target === 'value' && this.isAmplifyValueTargetDisabled(eventDef)) {
          throw new Error('行为条件中不存在带值条件时，不可选择上报值*N')
        }
        this.applyAmplifyImportConfig(eventDef, importedConfig)
        this.closeAmplifyImportDialog(eventDef)
        this.$message.success('导入成功，已覆盖当前放大策略配置')
      } catch (error) {
        console.error('解析放大策略导入文件失败:', error)
        this.$set(amplify, 'importError', error && error.message ? error.message : '解析失败，请检查导入文件格式')
      }
    },

    parseAmplifyImportFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = event => {
          try {
            const data = new Uint8Array(event.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            const sheet = workbook.Sheets[workbook.SheetNames[0]]
            const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' })
            const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
            const importedConfig = this.parseAmplifyLongTableRows(rows) || this.parseAmplifyPageTableRows(matrix)
            this.assertAmplifyImportConfig(importedConfig)
            resolve(importedConfig)
          } catch (e) {
            reject(e)
          }
        }
        reader.onerror = reject
        reader.readAsArrayBuffer(file)
      })
    },

    parseAmplifyLongTableRows(rows) {
      const grouped = {}
      let dimension = ''
      let target = ''
      ;(rows || []).forEach(row => {
        const country = this.pickAmplifyImportValue(row, ['country', '国家地区', '地区', 'geo', '国家'])
        const level = this.pickAmplifyImportValue(row, ['level', '层级', '层级编号', '档位'])
        const thresholdMatch = this.pickAmplifyImportMatchedValue(row, ['累计广告收入', '累计有效登录次数'])
        const multiplierMatch = this.pickAmplifyImportMatchedValue(row, ['上报次数*N', '上报值*N'])
        const threshold = thresholdMatch.value
        const multiplier = multiplierMatch.value
        if (country === '' || (threshold === '' && multiplier === '')) return
        if (!dimension) dimension = this.resolveAmplifyImportDimension(thresholdMatch.key)
        if (!target) target = this.resolveAmplifyImportTarget(multiplierMatch.key)
        const key = this.normalizeAmplifyImportCountry(country)
        if (!grouped[key]) grouped[key] = []
        grouped[key].push({
          levelIndex: this.parseAmplifyImportLevelIndex(level, grouped[key].length),
          threshold: String(threshold).trim(),
          multiplier: String(multiplier).trim(),
        })
      })
      return this.buildAmplifyImportConfigFromGroupedLevels(grouped, { dimension, target })
    },

    parseAmplifyPageTableRows(matrix) {
      const rows = (matrix || [])
        .map((row, index) => ({ row, rowIndex: index }))
        .filter(item => Array.isArray(item.row) && item.row.some(cell => String(cell).trim() !== ''))
      const countryHeaderIndex = rows.findIndex(item =>
        item.row.some(cell => this.normalizeAmplifyImportText(cell) === '国家地区'),
      )
      if (countryHeaderIndex < 0 || countryHeaderIndex + 2 >= rows.length) return null
      const levelHeaderItem = rows[countryHeaderIndex]
      const subHeaderItem = rows[countryHeaderIndex + 1]
      const levelHeaderRow = levelHeaderItem.row
      const subHeaderRow = subHeaderItem.row
      const dataRows = rows.slice(countryHeaderIndex + 2)
      const levelColumns = []
      for (let colIndex = 1; colIndex < Math.max(levelHeaderRow.length, subHeaderRow.length); colIndex++) {
        const levelText = this.normalizeAmplifyImportText(levelHeaderRow[colIndex])
        if (this.isAmplifyImportLevelHeader(levelText)) {
          this.assertAmplifyImportHeaderCell(subHeaderRow[colIndex], subHeaderItem.rowIndex, colIndex, 'dimension')
          this.assertAmplifyImportHeaderCell(subHeaderRow[colIndex + 1], subHeaderItem.rowIndex, colIndex + 1, 'target')
          levelColumns.push({ thresholdIndex: colIndex, multiplierIndex: colIndex + 1 })
          colIndex += 1
        }
      }
      if (!levelColumns.length) return null
      const firstLevelColumn = levelColumns[0]
      const dimension = this.resolveAmplifyImportDimension(subHeaderRow[firstLevelColumn.thresholdIndex])
      const target = this.resolveAmplifyImportTarget(subHeaderRow[firstLevelColumn.multiplierIndex])
      const grouped = {}
      dataRows.forEach(item => {
        const row = item.row
        const country = this.normalizeAmplifyImportCountry(row[0])
        if (!country) return
        const levels = []
        levelColumns.forEach((column, index) => {
          const threshold = row[column.thresholdIndex]
          const multiplier = row[column.multiplierIndex]
          levels.push({
            levelIndex: index,
            threshold: String(threshold).trim(),
            multiplier: String(multiplier).trim(),
          })
        })
        grouped[country] = levels
      })
      return this.buildAmplifyImportConfigFromGroupedLevels(grouped, { dimension, target })
    },

    buildAmplifyImportConfigFromGroupedLevels(grouped, meta = {}) {
      const countries = Object.keys(grouped || {})
      if (!countries.length) return null
      const defaultKey = countries.find(country => country.toLowerCase() === 'default')
      if (!defaultKey) {
        throw new Error('导入文件必须包含 default 国家地区配置')
      }
      const normalizeLevels = levels => {
        const sourceLevels = levels || []
        const maxLevelIndex = sourceLevels.reduce((max, item) => Math.max(max, Number(item.levelIndex) || 0), 0)
        const normalized = Array.from({ length: maxLevelIndex + 1 }, () => createAmplifyLevel())
        sourceLevels.forEach(item => {
          const levelIndex = Number(item.levelIndex) || 0
          normalized[levelIndex] = createAmplifyLevel(item)
        })
        return normalized
      }
      return {
        dimension: meta.dimension || '',
        target: meta.target || '',
        defaultLevels: normalizeLevels(grouped[defaultKey]),
        countryLevels: countries
          .filter(country => country !== defaultKey)
          .map(country => ({ country, levels: normalizeLevels(grouped[country]) })),
      }
    },

    assertAmplifyImportConfig(config) {
      if (!config || !Array.isArray(config.defaultLevels) || config.defaultLevels.length === 0) {
        throw new Error('未解析到有效放大策略配置，请检查表格结构是否包含国家地区、层级和数值列')
      }
      const levelCount = config.defaultLevels.length
      const checkLevels = (levels, label) => {
        if (!Array.isArray(levels) || levels.length !== levelCount) {
          throw new Error(`${label}层级数量须与 default 保持一致`)
        }
        levels.forEach((level, index) => {
          const valueState = this.getAmplifyLevelValueState(level)
          if (valueState.isPartial) {
            throw new Error(`${label}层级${index + 1}的两个字段必须同时为空或同时有值`)
          }
          if (valueState.isEmpty && (index === 0 || index < levelCount - 1)) {
            throw new Error(`${label}层级${index + 1}不能为空`)
          }
        })
      }
      checkLevels(config.defaultLevels, 'default')
      ;(config.countryLevels || []).forEach(item => {
        if (!item.country) throw new Error('国家地区不能为空')
        checkLevels(item.levels, item.country)
      })
    },

    pickAmplifyImportValue(row, keys) {
      return this.pickAmplifyImportMatchedValue(row, keys).value
    },

    pickAmplifyImportMatchedValue(row, keys) {
      const rowKeys = Object.keys(row)
      for (let i = 0; i < keys.length; i++) {
        const target = this.normalizeAmplifyImportText(keys[i])
        const matchedKey = rowKeys.find(key => this.normalizeAmplifyImportText(key) === target)
        if (matchedKey && row[matchedKey] !== undefined && row[matchedKey] !== null && row[matchedKey] !== '') {
          return { key: matchedKey, value: row[matchedKey] }
        }
      }
      return { key: '', value: '' }
    },

    normalizeAmplifyImportText(value) {
      return String(value === undefined || value === null ? '' : value)
        .replace(/\s+/g, '')
        .trim()
        .toLowerCase()
    },

    normalizeAmplifyImportCountry(value) {
      const text = String(value === undefined || value === null ? '' : value).trim()
      return text.toLowerCase() === 'default' ? 'default' : text
    },

    parseAmplifyImportLevelIndex(value, fallbackIndex) {
      const matched = String(value === undefined || value === null ? '' : value).match(/\d+/)
      return matched ? Math.max(0, Number(matched[0]) - 1) : fallbackIndex
    },

    resolveAmplifyImportDimension(headerText) {
      const text = this.normalizeAmplifyImportExactText(headerText)
      if (text === '累计广告收入') return 'ad_revenue'
      if (text === '累计有效登录次数') return 'login_count'
      return ''
    },

    resolveAmplifyImportTarget(headerText) {
      const text = this.normalizeAmplifyImportExactText(headerText)
      if (text === '上报次数*N') return 'count'
      if (text === '上报值*N') return 'value'
      return ''
    },

    normalizeAmplifyImportExactText(value) {
      return String(value === undefined || value === null ? '' : value).trim()
    },

    assertAmplifyImportHeaderCell(value, rowIndex, colIndex, type) {
      const matched =
        type === 'dimension' ? this.resolveAmplifyImportDimension(value) : this.resolveAmplifyImportTarget(value)
      if (!matched) {
        throw new Error(`${rowIndex + 1}行${colIndex + 1}列填写错误，请检查`)
      }
      return matched
    },

    isAmplifyImportThresholdHeader(text) {
      return !!this.resolveAmplifyImportDimension(text)
    },

    isAmplifyImportMultiplierHeader(text) {
      return !!this.resolveAmplifyImportTarget(text)
    },

    isAmplifyImportLevelHeader(text) {
      return /^(层级|档位)\d+$/i.test(text)
    },

    applyAmplifyImportConfig(eventDef, config) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      if (config.dimension) {
        this.$set(amplify, 'dimension', config.dimension)
      }
      if (config.target) {
        this.$set(amplify, 'target', config.target)
      }
      const defaultLevels = this.normalizeAmplifyLevels(config.defaultLevels || config.levels || [], 0)
      const levelCount = defaultLevels.length
      this.$set(amplify, 'defaultLevels', defaultLevels)
      this.$set(
        amplify,
        'countryLevels',
        (config.countryLevels || []).map(item => ({
          country: item.country || '',
          levels: this.normalizeAmplifyLevels(item.levels || [], levelCount),
        })),
      )
      this.syncAmplifyTargetByBehavior(eventDef)
      this.normalizeAmplifyIntegerValues(eventDef)
    },

    onWindowTypeChange(condition) {
      if (condition.windowType === 'unlimited') {
        this.$set(condition, 'windowDays', '')
      }
    },

    queryWindowDaySuggestions(queryString, callback) {
      const keyword = String(queryString || '').trim()
      const suggestions = keyword
        ? this.windowDayOptions.filter(item => item.value.indexOf(keyword) === 0)
        : this.windowDayOptions
      callback(suggestions)
    },

    onWindowDaysSelect(condition, item) {
      this.$set(condition, 'windowDays', item.value)
    },

    onReportWindowChange(eventDef) {
      if (eventDef.reportWindowEnabled && !eventDef.reportWindowHours) {
        this.$set(eventDef, 'reportWindowHours', 24)
      }
    },

    onReportWindowHoursInput(eventDef) {
      const value = String(eventDef.reportWindowHours).replace(/[^\d]/g, '')
      this.$set(eventDef, 'reportWindowHours', value ? Number(value) : '')
    },

    onReportWindowHoursBlur(eventDef) {
      if (eventDef.reportWindowEnabled && !this.isValidReportWindowHours(eventDef.reportWindowHours)) {
        this.$set(eventDef, 'reportWindowHours', 24)
      }
    },

    isValidWindowDays(value) {
      if (value === '' || value === undefined || value === null) return false
      const num = Number(value)
      return Number.isInteger(num) && num >= 1 && num <= 30
    },

    resolveWindowConfig(item) {
      const windowType = item.windowType || 'unlimited'
      return {
        windowType,
        windowDays:
          windowType !== 'unlimited' && item.windowDays !== undefined && item.windowDays !== null
            ? String(item.windowDays)
            : '',
      }
    },

    async fetchGameModeIntersect() {
      if (this.formData.products.length === 0) {
        this.subFilterPlayNameOpts = []
        this.subFilterModeNameOpts = []
        return
      }
      try {
        const params = {
          productIds: this.formData.products || [],
        }
        const res = await getGameModeIntersect(params)
        if (res && (res.code === '0000' || res.code === 200 || res.code === 0) && Array.isArray(res.data)) {
          const data = res.data

          const playNames = [...new Set(data.map(item => item.gameName).filter(Boolean))]
          const modeNames = [...new Set(data.map(item => item.modeName).filter(Boolean))]

          this.subFilterPlayNameOpts = playNames.map(name => ({ label: name, value: name }))
          this.subFilterModeNameOpts = modeNames.map(name => ({ label: name, value: name }))
        } else {
          this.subFilterPlayNameOpts = []
          this.subFilterModeNameOpts = []
        }
      } catch (error) {
        console.error('Failed to fetch game mode intersect:', error)
      }
    },

    async fetchAdzCodeIntersect() {
      if (this.formData.products.length === 0) {
        this.subFilterAdTypeOpts = []
        return
      }
      try {
        const params = {
          productIds: this.formData.products || [],
        }
        const res = await getAdzCodeIntersect(params)
        if (res && (res.code === '0000' || res.code === 200 || res.code === 0) && Array.isArray(res.data)) {
          const data = res.data
          const optionMap = new Map()
          data.forEach(item => {
            const value = typeof item === 'string' ? item : item.adzCode
            const label = typeof item === 'string' ? item : item.adzCodeType || value
            if (value) optionMap.set(value, { label, value })
          })
          this.subFilterAdTypeOpts = Array.from(optionMap.values())
        } else {
          this.subFilterAdTypeOpts = []
        }
      } catch (error) {
        console.error('Failed to fetch adz code intersect:', error)
      }
    },

    // ==================== 产品选择 ====================
    toggleSelectAllProducts(checked) {
      if (checked) {
        this.formData.products = this.productList.map(p => p.afid || p.id)
      } else {
        this.formData.products = []
      }
    },

    // ==================== 事件定义管理 ====================
    toggleEventCollapse(defIndex) {
      this.$set(this.eventDefinitions[defIndex], 'collapsed', !this.eventDefinitions[defIndex].collapsed)
    },

    addEventDef() {
      const eventDef = createEmptyEventDef()
      this.syncReportPlatformsByBehavior(eventDef, true)
      this.eventDefinitions.push(eventDef)
      this.$nextTick(() => {
        const cards = this.$el.querySelectorAll('.event-definition-card')
        if (cards.length > 0) {
          cards[cards.length - 1].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    },

    deleteEventDef(defIndex) {
      if (this.eventDefinitions.length <= 1) return this.$message.warning('至少需要保留一个事件')
      this.$confirm('确定要删除这个事件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.eventDefinitions.splice(defIndex, 1)
        })
        .catch(() => {})
    },

    copyLastEvent() {
      if (this.eventDefinitions.length === 0) return
      const source = this.eventDefinitions[this.eventDefinitions.length - 1]
      const copy = JSON.parse(JSON.stringify(source))
      copy.collapsed = false
      if (!copy.amplify) {
        copy.amplify = createEmptyAmplifyConfig()
      } else {
        copy.amplify.importDialogVisible = false
        copy.amplify.importFile = null
        copy.amplify.importFileName = ''
        copy.amplify.importError = ''
      }
      this.syncReportPlatformsByBehavior(copy)
      this.eventDefinitions.push(copy)
      this.$nextTick(() => {
        const cards = this.$el.querySelectorAll('.event-definition-card')
        if (cards.length > 0) {
          cards[cards.length - 1].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    },

    // ==================== 用户属性 ====================
    toggleUserAttrLogic(defIndex) {
      const eventDef = this.eventDefinitions[defIndex]
      this.$set(eventDef, 'userLogicType', eventDef.userLogicType === 'and' ? 'or' : 'and')
    },

    getUserAttrConfig(typeValue) {
      return this.userAttributeTypes.find(item => item.value === typeValue)
    },

    isMultipleUserAttr(typeValue) {
      return ['country_code', 'media_source'].includes(typeValue)
    },

    getAvailableUserAttrTypes(defIndex, currentIndex) {
      const attrs = this.eventDefinitions[defIndex].userAttributes
      const selectedTypes = attrs.map((item, index) => (index !== currentIndex ? item.typeValue : null)).filter(Boolean)
      return this.userAttributeTypes.filter(type => !selectedTypes.includes(type.value))
    },

    onUserAttrTypeChange(defIndex, item, attrIndex) {
      const attrs = this.eventDefinitions[defIndex].userAttributes
      const dupIndex = attrs.findIndex((a, i) => i !== attrIndex && a.typeValue === item.typeValue && a.typeValue)
      if (dupIndex !== -1) {
        this.$message.warning('该用户属性已存在，请选择其他属性')
        item.typeValue = ''
        return
      }
      const config = this.getUserAttrConfig(item.typeValue)
      if (config) {
        if (!config.hasOperator) item.operatorValue = ''
        if (!config.hasValue) {
          item.value = ''
        } else if (this.isMultipleUserAttr(item.typeValue)) {
          item.value = []
        } else if (item.typeValue === 'retention_user') {
          item.value = 1
        } else {
          item.value = ''
        }
      }
    },

    addUserAttribute(defIndex) {
      const eventDef = this.eventDefinitions[defIndex]
      const attrs = eventDef.userAttributes

      for (let i = 0; i < attrs.length; i++) {
        const item = attrs[i]
        if (!item.typeValue) {
          this.$message.warning(`请先完善第${i + 1}条用户属性的类型`)
          return
        }
        const config = this.getUserAttrConfig(item.typeValue)
        if (config) {
          if (config.hasOperator && !item.operatorValue) {
            this.$message.warning(`请先完善第${i + 1}条用户属性的比较关系`)
            return
          }
          if (config.hasValue && !item.value && item.value !== 0) {
            this.$message.warning(`请先完善第${i + 1}条用户属性的值`)
            return
          }
        }
      }

      if (attrs.length >= this.userAttributeTypes.length) {
        this.$message.warning('用户属性已全部添加')
        return
      }

      attrs.push({ typeValue: '', operatorValue: '', value: '', logicType: 'and' })
    },

    deleteUserAttribute(defIndex, attrIndex) {
      this.$confirm('确定要删除这个用户属性条件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.eventDefinitions[defIndex].userAttributes.splice(attrIndex, 1)
        })
        .catch(() => {})
    },

    // ==================== 行为条件组 ====================
    toggleBehaviorGroupLogic(defIndex) {
      const eventDef = this.eventDefinitions[defIndex]
      this.$set(eventDef, 'behaviorGroupLogicType', eventDef.behaviorGroupLogicType === 'and' ? 'or' : 'and')
    },

    toggleGroupConditionLogic(defIndex, groupIndex) {
      const group = this.eventDefinitions[defIndex].behaviorGroups[groupIndex]
      const newType = group.logicType === 'and' ? 'or' : 'and'
      this.$set(group, 'logicType', newType)
      group.conditions.forEach(bc => {
        bc.logicType = newType
      })
    },

    addBehaviorGroup(defIndex) {
      const group = createEmptyBehaviorGroup()
      group.conditions.push(createEmptyBehaviorCondition('and'))
      this.eventDefinitions[defIndex].behaviorGroups.push(group)
    },

    deleteBehaviorGroup(defIndex, groupIndex) {
      if (this.eventDefinitions[defIndex].behaviorGroups.length <= 1) {
        this.$message.warning('至少需要保留一个条件组')
        return
      }
      this.$confirm('确定要删除这个条件组吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          const eventDef = this.eventDefinitions[defIndex]
          eventDef.behaviorGroups.splice(groupIndex, 1)
          this.syncReportPlatformsByBehavior(eventDef)
          this.syncAmplifyDimensionByBehavior(eventDef)
          this.syncAmplifyTargetByBehavior(eventDef)
        })
        .catch(() => {})
    },

    addConditionToGroup(defIndex, groupIndex) {
      const group = this.eventDefinitions[defIndex].behaviorGroups[groupIndex]
      const bcs = group.conditions

      for (let i = 0; i < bcs.length; i++) {
        const item = bcs[i]
        if (!item.showCalculator && !item.behaviorTypeValue) {
          this.$message.warning(`请先完善第${i + 1}条行为条件的行为类型`)
          return
        }
        if (item.windowType !== 'unlimited' && !this.isValidWindowDays(item.windowDays)) {
          this.$message.warning(`请先完善第${i + 1}条行为条件的窗口期天数，范围为1~30的整数`)
          return
        }
        const isIncrementRevenueType = this.isIncrementRevenueBehavior(item)
        if (!isIncrementRevenueType && !item.operatorValue) {
          this.$message.warning(`请先完善第${i + 1}条行为条件的比较关系`)
          return
        }
        if (!item.value && item.value !== 0) {
          this.$message.warning(`请先完善第${i + 1}条行为条件的阈值`)
          return
        }
        if (this.isRangeOperator(item.operatorValue) && !item.value2) {
          this.$message.warning(`请先完善第${i + 1}条行为条件的区间最大值`)
          return
        }
      }

      bcs.push(createEmptyBehaviorCondition(group.logicType))
    },

    deleteConditionFromGroup(defIndex, groupIndex, bcIndex) {
      if (this.eventDefinitions[defIndex].behaviorGroups[groupIndex].conditions.length <= 1) {
        this.$message.warning('至少需要保留一个行为条件')
        return
      }
      this.$confirm('确定要删除这个行为条件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          const eventDef = this.eventDefinitions[defIndex]
          eventDef.behaviorGroups[groupIndex].conditions.splice(bcIndex, 1)
          this.syncReportPlatformsByBehavior(eventDef)
          this.syncAmplifyDimensionByBehavior(eventDef)
          this.syncAmplifyTargetByBehavior(eventDef)
        })
        .catch(() => {})
    },

    // ==================== 国家地区阈值覆盖 ====================
    toggleCountryThresholds(bc) {
      const showCountryThresholds = !bc.showCountryThresholds
      this.$set(bc, 'showCountryThresholds', showCountryThresholds)
      if (showCountryThresholds && !Array.isArray(bc.countryThresholds)) {
        this.$set(bc, 'countryThresholds', [])
      }
    },

    addCountryThreshold(bc) {
      if (!Array.isArray(bc.countryThresholds)) this.$set(bc, 'countryThresholds', [])
      const selectedCountries = bc.countryThresholds.map(item => item.country).filter(Boolean)
      if (this.countryList.length > 0 && selectedCountries.length >= this.countryList.length) {
        this.$message.warning('可配置的国家地区均已添加')
        return
      }
      bc.countryThresholds.push({ country: '', value: '', value2: '' })
    },

    removeCountryThreshold(bc, countryIndex) {
      if (!Array.isArray(bc.countryThresholds)) return
      bc.countryThresholds.splice(countryIndex, 1)
    },

    getAvailableCountryThresholdCountries(bc, currentIndex) {
      const selectedCountries = (bc.countryThresholds || [])
        .map((item, index) => (index !== currentIndex ? String(item.country || '').trim().toUpperCase() : ''))
        .filter(Boolean)
      return this.countryList.filter(item => {
        const country = String(item.value || '').trim().toUpperCase()
        return country && !selectedCountries.includes(country)
      })
    },

    isRangeOperator(operator) {
      return ['range', 'between'].includes(String(operator || '').toLowerCase())
    },

    isCollectionOperator(operator) {
      return ['contains', 'in'].includes(String(operator || '').toLowerCase())
    },

    getCollectionThresholdValues(value) {
      const values = Array.isArray(value) ? value : String(value === undefined || value === null ? '' : value).split(/[,，]/)
      return values.map(item => String(item).trim()).filter(Boolean)
    },

    getComparisonOperatorLabel(operator) {
      const option = this.operators.find(item => item.value === operator)
      return option ? option.label : operator
    },

    getCountryThresholdOperator(bc) {
      return this.isIncrementRevenueBehavior(bc) ? 'gte' : bc.operatorValue || ''
    },

    normalizeCountryThresholds(countryThresholds) {
      if (!Array.isArray(countryThresholds)) return []
      return countryThresholds.map(item => {
        let thresholdValue = item && item.thresholdValue
        if (typeof thresholdValue === 'string') {
          try {
            thresholdValue = JSON.parse(thresholdValue)
          } catch (error) {
            console.error('解析国家地区阈值失败:', error)
            thresholdValue = {}
          }
        }
        thresholdValue = thresholdValue || {}
        return {
          country: String((item && item.country) || '').trim().toUpperCase(),
          value: Array.isArray(thresholdValue.value)
            ? thresholdValue.value.join(',')
            : thresholdValue.value !== undefined && thresholdValue.value !== null
              ? thresholdValue.value
              : thresholdValue.min,
          value2: thresholdValue.max !== undefined && thresholdValue.max !== null ? thresholdValue.max : '',
        }
      })
    },

    buildCountryThresholds(bc, comparisonOperator) {
      return (bc.countryThresholds || []).map(item => ({
        country: String(item.country || '').trim().toUpperCase(),
        thresholdValue: this.buildThresholdValue(
          {
            operatorValue: comparisonOperator,
            value: item.value,
            value2: item.value2,
          },
          comparisonOperator,
        ),
      }))
    },

    validateCountryThresholds(bc, label) {
      const countryThresholds = bc.countryThresholds || []
      const countries = []
      const comparisonOperator = this.getCountryThresholdOperator(bc)
      for (let index = 0; index < countryThresholds.length; index++) {
        const countryThreshold = countryThresholds[index]
        const country = String(countryThreshold.country || '').trim().toUpperCase()
        const countryLabel = `${label}第 ${index + 1} 条地区阈值`
        if (!country) return { ok: false, message: `${countryLabel}的国家地区未选择` }
        if (countries.includes(country)) return { ok: false, message: `${label}的国家地区配置重复，请修改` }
        if (
          this.countryList.length > 0 &&
          !this.countryList.some(item => String(item.value || '').trim().toUpperCase() === country)
        ) {
          return { ok: false, message: `${countryLabel}的国家地区无效` }
        }
        countries.push(country)
        if (countryThreshold.value === '' || countryThreshold.value === undefined || countryThreshold.value === null) {
          return { ok: false, message: `${countryLabel}的阈值未填写` }
        }
        if (this.isCollectionOperator(comparisonOperator) && this.getCollectionThresholdValues(countryThreshold.value).length === 0) {
          return { ok: false, message: `${countryLabel}的集合阈值未填写` }
        }
        if (
          this.isRangeOperator(comparisonOperator) &&
          (countryThreshold.value2 === '' || countryThreshold.value2 === undefined || countryThreshold.value2 === null)
        ) {
          return { ok: false, message: `${countryLabel}的区间最大值未填写` }
        }
      }
      return { ok: true }
    },

    // ==================== 从属筛选条件 ====================
    isLevelRelatedBehavior(behaviorTypeValue) {
      return LEVEL_RELATED_BEHAVIOR_CODES.includes(behaviorTypeValue)
    },

    isAdTypeFilterBehavior(condition) {
      return AD_TYPE_FILTER_BEHAVIOR_CODES.includes(condition.behaviorTypeValue)
    },

    isSubFilterableBehavior(condition) {
      return this.getSubFilterFieldsForBehavior(condition).length > 0
    },

    getSubFilterFieldsForBehavior(condition) {
      if (this.isLevelRelatedBehavior(condition.behaviorTypeValue)) {
        return this.subFilterFields
      }
      if (this.isAdTypeFilterBehavior(condition)) {
        return [{ value: 'ad_type', label: '广告类型' }]
      }
      return []
    },

    addSubFilter(defIndex, groupIndex, bcIndex) {
      const bc = this.eventDefinitions[defIndex].behaviorGroups[groupIndex].conditions[bcIndex]
      if (!this.isSubFilterableBehavior(bc)) return
      if (!bc.subFilters) this.$set(bc, 'subFilters', [])
      const usedFields = bc.subFilters.map(sf => sf.field)
      const availableFields = this.getSubFilterFieldsForBehavior(bc)
      const available = availableFields.find(f => !usedFields.includes(f.value))
      if (!available) {
        this.$message.warning('可用筛选条件均已添加，无法继续添加筛选条件')
        return
      }
      bc.subFilters.push({ field: available.value, operator: 'eq', value: [] })
    },

    deleteSubFilter(defIndex, groupIndex, bcIndex, sfIndex) {
      const bc = this.eventDefinitions[defIndex].behaviorGroups[groupIndex].conditions[bcIndex]
      bc.subFilters.splice(sfIndex, 1)
    },

    onSubFilterFieldChange(defIndex, groupIndex, bcIndex, sfIndex) {
      this.eventDefinitions[defIndex].behaviorGroups[groupIndex].conditions[bcIndex].subFilters[sfIndex].value = []
    },

    getAvailableSubFilterFields(defIndex, groupIndex, bcIndex, currentSfIndex) {
      const bc = this.eventDefinitions[defIndex].behaviorGroups[groupIndex].conditions[bcIndex]
      const usedFields = (bc.subFilters || []).map((sf, i) => (i !== currentSfIndex ? sf.field : null)).filter(Boolean)
      return this.getSubFilterFieldsForBehavior(bc).filter(f => !usedFields.includes(f.value))
    },

    getSubFilterValueOpts(field) {
      if (field === 'ad_type') return this.subFilterAdTypeOpts
      return field === 'mode_name' ? this.subFilterModeNameOpts : this.subFilterPlayNameOpts
    },

    // ==================== 子集模式 ====================
    toggleSubProject(defIndex, groupIndex, bcIndex) {
      const bc = this.eventDefinitions[defIndex].behaviorGroups[groupIndex].conditions[bcIndex]
      const isShow = !bc.showSubProject
      this.$set(bc, 'showSubProject', isShow)
      if (isShow && (!bc.subConditions || bc.subConditions.length === 0)) {
        this.$set(bc, 'subConditions', [])
        this.$set(bc, 'subConditionLogicType', 'and')
        this.addSubCondition(defIndex, groupIndex, bcIndex)
      } else if (!isShow) {
        this.$set(bc, 'subConditions', [])
        this.$set(bc, 'subConditionLogicType', 'and')
        const eventDef = this.eventDefinitions[defIndex]
        this.syncReportPlatformsByBehavior(eventDef)
        this.syncAmplifyDimensionByBehavior(eventDef)
        this.syncAmplifyTargetByBehavior(eventDef)
      }
    },

    toggleSubConditionLogic(defIndex, groupIndex, bcIndex) {
      const bc = this.eventDefinitions[defIndex].behaviorGroups[groupIndex].conditions[bcIndex]
      this.$set(bc, 'subConditionLogicType', bc.subConditionLogicType === 'and' ? 'or' : 'and')
    },

    addSubCondition(defIndex, groupIndex, bcIndex) {
      const bc = this.eventDefinitions[defIndex].behaviorGroups[groupIndex].conditions[bcIndex]
      if (!bc.subConditions) this.$set(bc, 'subConditions', [])

      if (bc.subConditions.length > 0) {
        for (let i = 0; i < bc.subConditions.length; i++) {
          const subItem = bc.subConditions[i]
          if (!subItem.behaviorTypeValue) {
            this.$message.warning(`请先完善第${i + 1}条子集条件的行为类型`)
            return
          }
          const isIncrementRevenueType = this.isIncrementRevenueBehavior(subItem)
          if (!isIncrementRevenueType && !subItem.operatorValue) {
            this.$message.warning(`请先完善第${i + 1}条子集条件的比较关系`)
            return
          }
          if (!subItem.value && subItem.value !== 0) {
            this.$message.warning(`请先完善第${i + 1}条子集条件的阈值`)
            return
          }
          if (subItem.operatorValue === 'range' && !subItem.value2) {
            this.$message.warning(`请先完善第${i + 1}条子集条件的区间最大值`)
            return
          }
        }
      }
      bc.subConditions.push({
        behaviorTypeKey: '',
        behaviorTypeValue: '',
        behaviorTypeLabel: '',
        operatorValue: '',
        value: '',
        value2: '',
      })
    },

    deleteSubCondition(defIndex, groupIndex, bcIndex, subIndex) {
      this.$confirm('确定要删除这个子集条件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          const eventDef = this.eventDefinitions[defIndex]
          eventDef.behaviorGroups[groupIndex].conditions[bcIndex].subConditions.splice(subIndex, 1)
          this.syncReportPlatformsByBehavior(eventDef)
          this.syncAmplifyDimensionByBehavior(eventDef)
          this.syncAmplifyTargetByBehavior(eventDef)
        })
        .catch(() => {})
    },

    // ==================== 公式编辑器 ====================
    getInputWidth(value) {
      if (!value || value.length === 0) return '4px'
      return `${Math.max(20, Math.min(300, value.length * 10 + 10))}px`
    },

    getBc(defIndex, groupIndex, bcIndex) {
      return this.eventDefinitions[defIndex].behaviorGroups[groupIndex].conditions[bcIndex]
    },

    handleCalculatorContainerClick(event, defIndex, groupIndex, bcIndex) {
      if (this.isViewMode) return
      this.saveCalculatorInputValue(defIndex, groupIndex, bcIndex)

      const bc = this.getBc(defIndex, groupIndex, bcIndex)
      const container = event.currentTarget
      const clickX = event.clientX
      const clickY = event.clientY
      const tokens = container.querySelectorAll('.calculator-tag, .calculator-text')

      if (tokens.length === 0) {
        this.$set(bc, 'calculatorCursorPosition', 0)
        this.focusCalculatorInput(defIndex, groupIndex, bcIndex)
        return
      }

      let closestTokenIndex = -1
      let closestDistance = Infinity
      let insertBefore = false

      tokens.forEach(token => {
        const rect = token.getBoundingClientRect()
        const tokenCenterX = rect.left + rect.width / 2
        const tokenCenterY = rect.top + rect.height / 2
        const distance = Math.sqrt(Math.pow(clickX - tokenCenterX, 2) + Math.pow(clickY - tokenCenterY, 2))
        if (distance < closestDistance) {
          closestDistance = distance
          closestTokenIndex = parseInt(token.getAttribute('data-token-index') || 0)
          insertBefore = clickX < tokenCenterX
        }
      })

      if (closestTokenIndex !== -1) {
        this.$set(bc, 'calculatorCursorPosition', insertBefore ? closestTokenIndex : closestTokenIndex + 1)
      } else {
        this.$set(bc, 'calculatorCursorPosition', (bc.calculatorTokens || []).length)
      }
      this.focusCalculatorInput(defIndex, groupIndex, bcIndex)
    },

    focusCalculatorInput(defIndex, groupIndex, bcIndex) {
      this.$nextTick(() => {
        const inputs = this.$refs.calculatorInput
        if (inputs && inputs.length > 0) {
          const targetInput = Array.from(inputs).find(
            input =>
              input.dataset.defIndex == defIndex &&
              input.dataset.groupIndex == groupIndex &&
              input.dataset.bcIndex == bcIndex,
          )
          if (targetInput) targetInput.focus()
        }
      })
    },

    saveCalculatorInputValue(defIndex, groupIndex, bcIndex) {
      const bc = this.getBc(defIndex, groupIndex, bcIndex)
      const inputValue = bc.calculatorInputValue
      if (inputValue && inputValue.trim()) {
        const cursorPos = bc.calculatorCursorPosition || 0
        const tokens = bc.calculatorTokens || []
        tokens.splice(cursorPos, 0, { type: 'text', value: inputValue.trim() })
        this.$set(bc, 'calculatorTokens', tokens)
        this.$set(bc, 'calculatorInputValue', '')
        this.$set(bc, 'calculatorCursorPosition', cursorPos + 1)
        this.updateCalculatorText(defIndex, groupIndex, bcIndex)
      }
    },

    removeCalculatorToken(defIndex, groupIndex, bcIndex, tokenIndex) {
      const bc = this.getBc(defIndex, groupIndex, bcIndex)
      const tokens = bc.calculatorTokens || []
      tokens.splice(tokenIndex, 1)
      this.$set(bc, 'calculatorTokens', tokens)
      const cursorPos = bc.calculatorCursorPosition
      if (cursorPos > tokenIndex) {
        this.$set(bc, 'calculatorCursorPosition', cursorPos - 1)
      }
      this.updateCalculatorText(defIndex, groupIndex, bcIndex)
    },

    handleCalculatorInputKeydown(event, defIndex, groupIndex, bcIndex) {
      const bc = this.getBc(defIndex, groupIndex, bcIndex)
      const value = event.target.value.trim()
      const cursorPos = bc.calculatorCursorPosition || 0

      if (event.key === 'Backspace' && !value) {
        event.preventDefault()
        const tokens = bc.calculatorTokens || []
        if (tokens.length > 0 && cursorPos > 0) {
          tokens.splice(cursorPos - 1, 1)
          this.$set(bc, 'calculatorTokens', tokens)
          this.$set(bc, 'calculatorCursorPosition', cursorPos - 1)
          this.updateCalculatorText(defIndex, groupIndex, bcIndex)
        }
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.saveCalculatorInputValue(defIndex, groupIndex, bcIndex)
        if (cursorPos > 0) {
          this.$set(bc, 'calculatorCursorPosition', cursorPos - 1)
          this.focusCalculatorInput(defIndex, groupIndex, bcIndex)
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.saveCalculatorInputValue(defIndex, groupIndex, bcIndex)
        const tokens = bc.calculatorTokens || []
        if (cursorPos < tokens.length) {
          this.$set(bc, 'calculatorCursorPosition', cursorPos + 1)
          this.focusCalculatorInput(defIndex, groupIndex, bcIndex)
        }
      }
    },

    updateCalculatorText(defIndex, groupIndex, bcIndex, includeInput = false) {
      const bc = this.getBc(defIndex, groupIndex, bcIndex)
      if (includeInput) {
        const inputValue = bc.calculatorInputValue
        if (inputValue && inputValue.trim()) {
          const tokens = bc.calculatorTokens || []
          tokens.push({ type: 'text', value: inputValue.trim() })
          this.$set(bc, 'calculatorTokens', tokens)
          this.$set(bc, 'calculatorInputValue', '')
        }
      }
      const tokens = bc.calculatorTokens || []
      let text = ''
      tokens.forEach(token => {
        if (token.type === 'metric') text += `{${token.label}}`
        else text += token.value
      })
      this.$set(bc, 'showCalculatorText', text)
      const eventDef = this.eventDefinitions[defIndex]
      this.syncReportPlatformsByBehavior(eventDef)
    },

    addCalculatorOperator(defIndex, groupIndex, bcIndex, operator) {
      const bc = this.getBc(defIndex, groupIndex, bcIndex)
      let cursorPos = bc.calculatorCursorPosition
      const tokens = [...(bc.calculatorTokens || [])]

      const inputValue = bc.calculatorInputValue
      if (inputValue && inputValue.trim()) {
        const insertPos = cursorPos !== undefined ? cursorPos : tokens.length
        tokens.splice(insertPos, 0, { type: 'text', value: inputValue.trim() })
        cursorPos = insertPos + 1
      }

      const operatorMap = { '×': '*', '÷': '/' }
      const actualOperator = operatorMap[operator] || operator
      const finalCursorPos = cursorPos !== undefined ? cursorPos : tokens.length
      tokens.splice(finalCursorPos, 0, { type: 'operator', value: actualOperator, label: operator })

      this.$set(bc, 'calculatorTokens', tokens)
      this.$set(bc, 'calculatorInputValue', '')
      this.$set(bc, 'calculatorCursorPosition', finalCursorPos + 1)
      this.updateCalculatorText(defIndex, groupIndex, bcIndex)
      this.focusCalculatorInput(defIndex, groupIndex, bcIndex)
    },

    insertEventConfirm(defIndex, groupIndex, bcIndex, eventTypeKey) {
      const bc = this.getBc(defIndex, groupIndex, bcIndex)
      const behavior = this.getBehaviorByKey(eventTypeKey)
      if (!behavior) return

      let cursorPos = bc.calculatorCursorPosition
      const tokens = [...(bc.calculatorTokens || [])]

      const inputValue = bc.calculatorInputValue
      if (inputValue && inputValue.trim()) {
        const insertPos = cursorPos !== undefined ? cursorPos : tokens.length
        tokens.splice(insertPos, 0, { type: 'text', value: inputValue.trim() })
        cursorPos = insertPos + 1
      }

      const finalCursorPos = cursorPos !== undefined ? cursorPos : tokens.length
      tokens.splice(finalCursorPos, 0, { type: 'metric', value: behavior.value, label: behavior.label })

      this.$set(bc, 'calculatorTokens', tokens)
      this.$set(bc, 'calculatorInputValue', '')
      this.$set(bc, 'calculatorCursorPosition', finalCursorPos + 1)
      this.$set(bc, 'showEventPopover', false)
      this.updateCalculatorText(defIndex, groupIndex, bcIndex)
      this.focusCalculatorInput(defIndex, groupIndex, bcIndex)
    },

    toggleCalculator(defIndex, groupIndex, bcIndex) {
      const bc = this.getBc(defIndex, groupIndex, bcIndex)
      this.$set(bc, 'showCalculator', !bc.showCalculator)
      if (bc.showCalculator) {
        const behavior =
          this.getBehaviorByKey(bc.behaviorTypeKey) ||
          this.behaviorTypes.find(item => item.value === bc.behaviorTypeValue)
        if (behavior) {
          this.$set(bc, 'calculatorTokens', [{ type: 'metric', value: behavior.value, label: behavior.label }])
          this.$set(bc, 'calculatorInputValue', '')
          this.$set(bc, 'calculatorCursorPosition', 1)
          this.updateCalculatorText(defIndex, groupIndex, bcIndex)
        }
      } else {
        this.$set(bc, 'calculatorTokens', [])
        this.$set(bc, 'calculatorInputValue', '')
        this.$set(bc, 'showCalculatorText', '')
        this.$set(bc, 'calculatorCursorPosition', 0)
        const eventDef = this.eventDefinitions[defIndex]
        this.syncReportPlatformsByBehavior(eventDef)
      }
    },

    // ==================== 数据加载 ====================
    async loadEnumData() {
      try {
        this.enumLoading = true
        const [enumRes, countryRes, mediaSourceRes] = await Promise.all([
          getBuyEventEnumAll(),
          getCountryListFetch({}),
          getmediaSourceFetch({}),
        ])

        if (countryRes.code === '0000' && countryRes.countryList) {
          this.countryList = (countryRes.countryList || []).map(item => ({
            label: item.name,
            value: String(item.paramGeo || item.geo || '').trim().toUpperCase(),
          }))
        }

        if (mediaSourceRes.code === '0000' && mediaSourceRes.mediaList) {
          this.mediaSourceList = (mediaSourceRes.mediaList || []).map(item => ({
            label: item,
            value: item,
          }))
        }

        const res = enumRes
        if (res.code === '0000' && res.data) {
          const attributeTypes = res.data.attributeType || []
          this.userAttributeTypes = attributeTypes.map(item => {
            const config = {
              label: item.desc,
              value: item.code,
              hasOperator: false,
              hasValue: false,
            }
            if (item.code === 'country_code') {
              config.hasOperator = true
              config.hasValue = true
              config.operators = [
                { label: '等于', value: 'equals' },
                { label: '不等于', value: 'not_equals' },
              ]
              config.valueType = 'select'
              config.valueOptions = this.countryList
            } else if (item.code === 'media_source') {
              config.hasOperator = true
              config.hasValue = true
              config.operators = [
                { label: '等于', value: 'equals' },
                { label: '不等于', value: 'not_equals' },
              ]
              config.valueType = 'select'
              config.valueOptions = this.mediaSourceList
            }
            return config
          })

          this.timeDimensions = (res.data.timeDimension || []).map(item => ({
            label: item.desc,
            value: item.code,
          }))

          this.windowTypeOptions = (res.data.windowType || []).map(item => ({
            label: item.desc,
            value: item.code,
          }))

          this.behaviorTypes = (res.data.behaviorType || []).map(item => ({
            label: item.desc,
            value: item.code,
            typeKey: this.buildBehaviorTypeKey(item),
            category: item.category,
          }))

          this.operators = (res.data.comparisonOperator || []).map(item => ({
            label: decodeHtmlEntities(item.desc),
            value: item.code,
          }))

          this.reportPlatformOptions = (res.data.reportPlatform || []).map(item => ({
            label: item.desc,
            value: item.code,
          }))

          this.productList = res.data.products || []
        } else {
          this.$message.error(res.message || '获取枚举数据失败')
        }
      } catch (error) {
        console.error('加载枚举数据失败:', error)
        this.$message.error('获取枚举数据失败')
      } finally {
        this.enumLoading = false
      }
    },

    async loadEventData(id) {
      try {
        const res = await getBuyEventMergedDetail({ id })
        if (res.code === '0000' && res.data) {
          const data = res.data

          const isCopyMode = this.mode === 'add' && !!this.copySourceId
          this.status = isCopyMode ? 0 : data.status !== undefined && data.status !== null ? data.status : 1

          const sourceProductId = this.normalizeProductId(data.productId)
          this.formData.products = isCopyMode && this.copyTargetProductId ? [this.copyTargetProductId] : [sourceProductId]

          const userAttributes = (data.userAttributes || []).map(item => {
            let value = ''
            if (item.conditionValue) {
              try {
                const parsedValue =
                  typeof item.conditionValue === 'string' ? JSON.parse(item.conditionValue) : item.conditionValue
                if (item.attributeType === 'retention_user' && Array.isArray(parsedValue) && parsedValue.length > 0) {
                  value = parsedValue[0]
                } else {
                  value = parsedValue
                }
              } catch (e) {
                value = item.conditionValue
              }
            }
            return {
              typeValue: item.attributeType || '',
              operatorValue: item.comparisonOperator || '',
              value: value,
              logicType: 'and',
            }
          })

          const behaviorGroups = (data.behaviorConditions || []).map(group => {
            const conditions = (group.conditions || []).map(item => {
              let value = ''
              let value2 = ''
              if (item.thresholdValue) {
                try {
                  const threshold =
                    typeof item.thresholdValue === 'string' ? JSON.parse(item.thresholdValue) : item.thresholdValue
                  if ('value' in threshold) {
                    value = Array.isArray(threshold.value) ? threshold.value.join(',') : threshold.value
                  } else if ('min' in threshold && 'max' in threshold) {
                    value = threshold.min
                    value2 = threshold.max
                  }
                } catch (e) {
                  console.error('解析 thresholdValue 失败:', e)
                }
              }
              const countryThresholds = this.normalizeCountryThresholds(item.countryThresholds)

              const hasCalculatorText = !!item.calculatorText
              let showCalculatorText = ''
              let calculatorTokens = []
              if (hasCalculatorText) {
                showCalculatorText = item.calculatorText
                calculatorTokens = this.parseDisplayTextToTokens(item.calculatorText)
              }

              const subConditions = []
              let subConditionLogicType = 'and'
              if (item.child) {
                try {
                  const childData = typeof item.child === 'string' ? JSON.parse(item.child) : item.child
                  if (childData && childData.conditions && childData.conditions.length > 0) {
                    subConditionLogicType = childData.logicType || 'and'
                    childData.conditions.forEach(subItem => {
                      let subValue = ''
                      let subValue2 = ''
                      if (subItem.thresholdValue) {
                        const threshold =
                          typeof subItem.thresholdValue === 'string'
                            ? JSON.parse(subItem.thresholdValue)
                            : subItem.thresholdValue
                        if (threshold.value !== undefined && threshold.value !== null) {
                          subValue = threshold.value
                        } else if (threshold.min !== undefined && threshold.max !== undefined) {
                          subValue = threshold.min
                          subValue2 = threshold.max
                        }
                      }
                      const subBehaviorLabel =
                        subItem.behaviorTypeDesc ||
                        subItem.behaviorTypeName ||
                        subItem.behaviorDesc ||
                        subItem.desc ||
                        ''
                      subConditions.push({
                        behaviorTypeKey: this.resolveBehaviorTypeKey(subItem.behaviorType || '', subBehaviorLabel),
                        behaviorTypeValue: subItem.behaviorType || '',
                        behaviorTypeLabel: subBehaviorLabel,
                        operatorValue: subItem.comparisonOperator || '',
                        value: subValue,
                        value2: subValue2,
                      })
                    })
                  }
                } catch (e) {
                  console.error('解析 child 字段失败:', e)
                }
              }

              // 解析 subFilters (回显玩法名称和模式名称)
              const subFilters = []
              if (item.gameplayNames && item.gameplayNames.length > 0) {
                subFilters.push({
                  field: 'play_name',
                  operator: item.gameplayOperator || 'eq',
                  value: Array.isArray(item.gameplayNames) ? item.gameplayNames : [item.gameplayNames],
                })
              }
              if (item.modeNames && item.modeNames.length > 0) {
                subFilters.push({
                  field: 'mode_name',
                  operator: item.modeOperator || 'eq',
                  value: Array.isArray(item.modeNames) ? item.modeNames : [item.modeNames],
                })
              }
              const adzCodes = item.adzCodes || item.adzcodes || item.adzCodeList || item.adTypeCodes
              if (adzCodes && adzCodes.length > 0) {
                subFilters.push({
                  field: 'ad_type',
                  operator: item.adzCodeOperator || item.adzcodeOperator || item.adTypeOperator || 'eq',
                  value: Array.isArray(adzCodes) ? adzCodes : [adzCodes],
                })
              }

              const behaviorLabel =
                item.behaviorTypeDesc || item.behaviorTypeName || item.behaviorDesc || item.desc || ''
              const windowConfig = this.resolveWindowConfig(item)
              return {
                timeDimensionValue: item.timeDimension || '',
                windowType: windowConfig.windowType,
                windowDays: windowConfig.windowDays,
                behaviorTypeKey: this.resolveBehaviorTypeKey(item.behaviorType || '', behaviorLabel),
                behaviorTypeValue: item.behaviorType || '',
                behaviorTypeLabel: behaviorLabel,
                operatorValue: item.comparisonOperator || '',
                value: value,
                value2: value2,
                countryThresholds: countryThresholds,
                showCountryThresholds: countryThresholds.length > 0,
                logicType: group.conditionLogic || 'and',
                showCalculator: hasCalculatorText,
                showCalculatorText: showCalculatorText,
                calculatorTokens: calculatorTokens,
                calculatorInputValue: '',
                calculatorCursorPosition: calculatorTokens.length,
                showSubProject: subConditions.length > 0,
                showEventPopover: false,
                subConditions: subConditions,
                subConditionLogicType: subConditionLogicType,
                subFilters: subFilters,
              }
            })

            return {
              logicType: group.conditionLogic || 'and',
              conditions: conditions,
            }
          })

          const reportPlatformSource =
            data.reportPlatformTexts !== undefined && data.reportPlatformTexts !== null
              ? data.reportPlatformTexts
              : data.reportPlatforms || data.reportPlatform || data.platforms
          const reportPlatforms = this.normalizeReportPlatforms(reportPlatformSource)
          const amplify = this.normalizeAmplifyConfig(data)
          const reportWindowHourValue = [
            data.reportWindowHours,
            data.windowHours,
            data.reportStartHours,
            data.startReportHours,
          ].find(item => item !== undefined && item !== null && item !== '')
          const reportWindowHours = reportWindowHourValue !== undefined ? reportWindowHourValue : 24
          const reportWindowEnabled =
            data.reportWindowEnabled !== undefined && data.reportWindowEnabled !== null
              ? data.reportWindowEnabled === true ||
                data.reportWindowEnabled === 1 ||
                data.reportWindowEnabled === '1' ||
                data.reportWindowEnabled === 'true'
              : reportWindowHourValue !== undefined && Number(reportWindowHours) > 0

          this.eventDefinitions = [
            {
              name: data.eventName || '',
              remark: data.notes || '',
              reportPlatforms,
              reportWindowEnabled,
              reportWindowHours,
              collapsed: false,
              userAttributes: userAttributes,
              userLogicType: 'and',
              behaviorGroups: behaviorGroups,
              behaviorGroupLogicType: data.behaviorGroupLogic || 'and',
              amplify,
            },
          ]
          this.eventDefinitions.forEach(eventDef => {
            if (!this.isViewMode) {
              this.syncReportPlatformsByBehavior(eventDef)
            }
            if (eventDef.amplify && eventDef.amplify.enabled) {
              this.syncAmplifyTargetByBehavior(eventDef)
              this.normalizeAmplifyIntegerValues(eventDef)
            }
          })
        }
      } catch (error) {
        console.error('加载事件数据失败:', error)
        this.$message.error('获取事件详情失败')
      }
    },

    // ==================== 校验与提交 ====================
    validateForm() {
      if (this.formData.products.length === 0) {
        this.$alert('请选择上报产品', '提示', { type: 'warning', confirmButtonText: '确定' })
        return false
      }

      if (this.eventDefinitions.length === 0) {
        this.$message.error('请至少添加一个事件定义')
        return false
      }

      // 先整体检查是否存在名称为空的事件块
      const hasEmptyName = this.eventDefinitions.some(def => !def.name || !def.name.trim())
      if (hasEmptyName) {
        this.$alert('请填写所有事件名称', '提示', { type: 'warning', confirmButtonText: '确定' })
        return false
      }

      const names = []
      for (let defIndex = 0; defIndex < this.eventDefinitions.length; defIndex++) {
        const eventDef = this.eventDefinitions[defIndex]
        const label = `第 ${defIndex + 1} 个事件`

        const eventNameCheck = this.validateEventName(eventDef.name, label)
        if (!eventNameCheck.ok) {
          this.$alert(eventNameCheck.message, '校验错误', { type: 'error', confirmButtonText: '确定' })
          return false
        }

        const nameKey = eventDef.name.trim().toLowerCase()
        if (names.includes(nameKey)) {
          this.$alert(`同一产品下事件名称不可重复：重复名称「${eventDef.name.trim()}」`, '校验错误', { type: 'error' })
          return false
        }
        names.push(nameKey)

        if (eventDef.reportWindowEnabled && !this.isValidReportWindowHours(eventDef.reportWindowHours)) {
          this.$alert(`${label}上报窗口小时数需填写正整数`, '校验错误', {
            type: 'error',
            confirmButtonText: '确定',
          })
          return false
        }

        if (!Array.isArray(eventDef.reportPlatforms) || eventDef.reportPlatforms.length === 0) {
          this.$alert(`${label}请选择上报平台`, '提示', { type: 'warning', confirmButtonText: '确定' })
          return false
        }
        if (
          this.hasValuedBehavior(eventDef) &&
          eventDef.reportPlatforms.some(platform => this.isAppsFlyerPlatform(platform))
        ) {
          this.$alert(`${label}包含带值行为条件时不可勾选 AppsFlyer`, '提示', {
            type: 'warning',
            confirmButtonText: '确定',
          })
          return false
        }

        if (eventDef.behaviorGroups.length === 0) {
          this.$message.error(`${label}至少需要一个条件组`)
          return false
        }

        for (let groupIndex = 0; groupIndex < eventDef.behaviorGroups.length; groupIndex++) {
          const group = eventDef.behaviorGroups[groupIndex]
          const groupLabel = `${label}第 ${groupIndex + 1} 个条件组`

          if (group.conditions.length === 0) {
            this.$message.error(`${groupLabel}至少需要一条行为条件`)
            return false
          }

          for (let bcIndex = 0; bcIndex < group.conditions.length; bcIndex++) {
            const bc = group.conditions[bcIndex]
            const bcLabel = `${groupLabel}第 ${bcIndex + 1} 条行为条件`

            if (!bc.showCalculator && !bc.behaviorTypeValue) {
              this.$message.error(`${bcLabel}的行为类型未选择`)
              return false
            }
            if (bc.showCalculator && !bc.showCalculatorText) {
              this.$message.error(`${bcLabel}的自定义公式不能为空`)
              return false
            }
            if (bc.windowType !== 'unlimited' && !this.isValidWindowDays(bc.windowDays)) {
              this.$message.error(`${bcLabel}的窗口期天数需填写1~30的整数`)
              return false
            }

            const isIncrementRevenueType = this.isIncrementRevenueBehavior(bc)
            if (!isIncrementRevenueType && !bc.operatorValue) {
              this.$message.error(`${bcLabel}的比较关系未选择`)
              return false
            }
            if (!bc.value && bc.value !== 0) {
              this.$message.error(`${bcLabel}的阈值未填写`)
              return false
            }
            if (this.isCollectionOperator(bc.operatorValue) && this.getCollectionThresholdValues(bc.value).length === 0) {
              this.$message.error(`${bcLabel}的集合阈值未填写`)
              return false
            }
            if (this.isRangeOperator(bc.operatorValue) && !bc.value2 && bc.value2 !== 0) {
              this.$message.error(`${bcLabel}的区间最大值未填写`)
              return false
            }

            const countryThresholdCheck = this.validateCountryThresholds(bc, bcLabel)
            if (!countryThresholdCheck.ok) {
              this.$message.error(countryThresholdCheck.message)
              return false
            }

            if (bc.subConditions && bc.subConditions.length > 0) {
              for (let j = 0; j < bc.subConditions.length; j++) {
                const subItem = bc.subConditions[j]
                if (!subItem.behaviorTypeValue) {
                  this.$message.error(`${bcLabel}第 ${j + 1} 条子集条件的行为类型未选择`)
                  return false
                }
                const isSubIncrementRevenue = this.isIncrementRevenueBehavior(subItem)
                if (!isSubIncrementRevenue && !subItem.operatorValue) {
                  this.$message.error(`${bcLabel}第 ${j + 1} 条子集条件的比较关系未选择`)
                  return false
                }
                if (!subItem.value && subItem.value !== 0) {
                  this.$message.error(`${bcLabel}第 ${j + 1} 条子集条件的阈值未填写`)
                  return false
                }
              }
            }

            if (bc.subFilters && bc.subFilters.length > 0) {
              for (let sfIndex = 0; sfIndex < bc.subFilters.length; sfIndex++) {
                const sf = bc.subFilters[sfIndex]
                if (!Array.isArray(sf.value) || sf.value.length === 0) {
                  this.$alert(`${bcLabel}第 ${sfIndex + 1} 条筛选条件的条件值未选择`, '校验错误', { type: 'error' })
                  return false
                }
              }
            }
          }
        }

        const amplifyCheck = this.validateAmplifyConfig(eventDef, label)
        if (!amplifyCheck.ok) {
          this.$alert(amplifyCheck.message, '校验错误', { type: 'error', confirmButtonText: '确定' })
          return false
        }
      }

      return true
    },

    validateEventName(name, label) {
      const eventName = String(name || '')
      if (eventName.length > EVENT_NAME_MAX_LENGTH) {
        return { ok: false, message: `${label}事件名称长度不能超过40字符` }
      }
      if (!EVENT_NAME_PATTERN.test(eventName)) {
        return { ok: false, message: `${label}事件名称只能包含数字、字母和下划线` }
      }
      return { ok: true }
    },

    isValidReportWindowHours(value) {
      const num = Number(value)
      return Number.isInteger(num) && num > 0
    },

    validateAmplifyConfig(eventDef, label) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      if (!amplify.enabled) return { ok: true }
      if (!amplify.target) return { ok: false, message: `${label}请选择放大对象` }
      if (!amplify.dimension) return { ok: false, message: `${label}请选择放大类型` }
      if (this.isAmplifyValueTargetDisabled(eventDef) && amplify.target === 'value') {
        return { ok: false, message: `${label}行为条件中不存在带值条件时，不可选择上报值*N` }
      }
      if (!Array.isArray(amplify.defaultLevels) || amplify.defaultLevels.length === 0) {
        return { ok: false, message: `${label}请至少添加一个放大档位` }
      }

      const defaultCheck = this.validateAmplifyLevels(
        amplify.defaultLevels,
        `${label}default`,
        amplify.dimension,
        amplify.target,
        amplify.defaultLevels.length,
      )
      if (!defaultCheck.ok) return defaultCheck

      const countries = []
      for (let i = 0; i < amplify.countryLevels.length; i++) {
        const countryConfig = amplify.countryLevels[i]
        if (!countryConfig.country) {
          return { ok: false, message: `${label}请选择国家地区` }
        }
        if (countries.includes(countryConfig.country)) {
          return { ok: false, message: `${label}国家地区配置重复，请修改` }
        }
        countries.push(countryConfig.country)
        const countryCheck = this.validateAmplifyLevels(
          countryConfig.levels || [],
          `${label}${countryConfig.country}`,
          amplify.dimension,
          amplify.target,
          amplify.defaultLevels.length,
        )
        if (!countryCheck.ok) return countryCheck
      }

      return { ok: true }
    },

    getAmplifyLevelValueState(level) {
      const threshold = String((level && level.threshold) || '').trim()
      const multiplier = String((level && level.multiplier) || '').trim()
      const thresholdEmpty = threshold === ''
      const multiplierEmpty = multiplier === ''
      return {
        threshold,
        multiplier,
        isEmpty: thresholdEmpty && multiplierEmpty,
        isPartial: thresholdEmpty !== multiplierEmpty,
      }
    },

    validateAmplifyLevels(levels, label, dimension, target, levelCount) {
      const totalLevelCount = Math.max(1, levelCount || (Array.isArray(levels) ? levels.length : 0))
      if (!Array.isArray(levels) || levels.length === 0) {
        return { ok: false, message: `${label}请至少添加一个放大档位` }
      }
      const thresholds = []
      let prevMultiplier = null
      let shouldWarnMultiplier = false
      let hasEmptyLevel = false
      for (let i = 0; i < totalLevelCount; i++) {
        const valueState = this.getAmplifyLevelValueState(levels[i])
        const threshold = valueState.threshold
        const multiplier = valueState.multiplier
        if (valueState.isPartial) {
          return { ok: false, message: `${label}层级${i + 1}的两个字段必须同时为空或同时有值` }
        }
        if (valueState.isEmpty) {
          if (i === 0 || i < totalLevelCount - 1) {
            return { ok: false, message: `${label}层级${i + 1}不能为空` }
          }
          hasEmptyLevel = true
          continue
        }
        if (!this.isValidAmplifyThreshold(threshold, dimension)) {
          return {
            ok: false,
            message:
              dimension === 'ad_revenue' ? '累计广告收入须为正数，且最多保留四位小数' : '累计有效登录次数须为正整数',
          }
        }
        if (!this.isValidAmplifyMultiplier(multiplier, target)) {
          return {
            ok: false,
            message: target === 'count' ? '上报次数须为大于0的整数' : '上报值的 N 须大于 1，且最多保留四位小数',
          }
        }
        if (thresholds.includes(threshold)) {
          return { ok: false, message: '存在重复的累计阈值，请修改' }
        }
        thresholds.push(threshold)
        const multiplierNum = Number(multiplier)
        if (prevMultiplier !== null && multiplierNum < prevMultiplier) shouldWarnMultiplier = true
        prevMultiplier = multiplierNum
      }
      if (!hasEmptyLevel && shouldWarnMultiplier) {
        this.$message.warning(`${label}的乘数 N 建议按层级递增`)
      }
      return { ok: true }
    },

    isValidAmplifyThreshold(value, dimension) {
      const text = String(value || '').trim()
      if (!text) return false
      if (dimension === 'ad_revenue') {
        return /^(?:\d+|\d+\.\d{1,4})$/.test(text) && Number(text) > 0
      }
      return /^[1-9]\d*$/.test(text)
    },

    isValidAmplifyMultiplier(value, target) {
      const text = String(value || '').trim()
      if (target === 'count') return /^[1-9]\d*$/.test(text)
      return /^(?:\d+|\d+\.\d{1,4})$/.test(text) && Number(text) > 1
    },

    hasLevelBehaviorWithoutPlayAndMode() {
      return (this.eventDefinitions || []).some(eventDef =>
        (eventDef.behaviorGroups || []).some(group =>
          (group.conditions || []).some(bc => {
            if (!this.isLevelRelatedBehavior(bc.behaviorTypeValue)) return false

            const subFilters = bc.subFilters || []
            const hasPlayNameFilter = subFilters.some(sf => sf.field === 'play_name')
            const hasModeNameFilter = subFilters.some(sf => sf.field === 'mode_name')
            return !hasPlayNameFilter && !hasModeNameFilter
          }),
        ),
      )
    },

    async handleSubmit() {
      if (!this.validateForm()) return
      if (this.hasLevelBehaviorWithoutPlayAndMode()) {
        try {
          await this.$confirm('存在关卡类型的行为条件未筛选玩法名称、模式名称，确认提交吗？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
          })
        } catch (e) {
          return
        }
      }

      try {
        this.saveLoading = true

        if (this.mode === 'add') {
          // 注意：前端已做页面内名称去重，但无法预先查询各产品下已有事件名称。
          // 若服务端检测到目标产品下名称冲突，会返回非 0000 code，res.message 中含具体冲突信息，以弹窗形式展示。
          const events = this.eventDefinitions.map(eventDef => {
            this.flushCalculatorInputs(eventDef)
            return this.buildCreateEventParams(eventDef)
          })

          const saveData = {
            events,
            productIds: this.formData.products || [],
          }

          const res = await createBuyEventMerged(saveData)
          if (res.code === '0000') {
            this.$message.success('提交成功')
            this.submitted = true
            setTimeout(() => this.handleGoBack(), 500)
          }
        } else if (this.mode === 'edit') {
          const eventDef = this.eventDefinitions[0]
          this.flushCalculatorInputs(eventDef)
          const event = this.buildUpdateEventParams(eventDef)
          event.id = Number(this.$route.query.id)

          const res = await updateBuyEventMerged(event)
          if (res.code === '0000') {
            this.$message.success('保存成功')
            this.submitted = true
            setTimeout(() => this.handleGoBack(), 500)
          }
        }
      } catch (error) {
        console.error('提交失败:', error)
        // this.$alert(error.msg || '提交失败，请稍后重试', '系统错误', { type: 'error' })
      } finally {
        this.saveLoading = false
      }
    },

    // 提交前把所有计算器输入框未提交的内容刷入 tokens
    flushCalculatorInputs(eventDef) {
      const groups = eventDef.behaviorGroups || []
      groups.forEach(group => {
        const conditions = group.conditions || []
        conditions.forEach(bc => {
          if (bc.showCalculator && bc.calculatorInputValue && bc.calculatorInputValue.trim()) {
            const tokens = bc.calculatorTokens || []
            tokens.push({ type: 'text', value: bc.calculatorInputValue.trim() })
            bc.calculatorTokens = tokens
            bc.calculatorInputValue = ''
          }
          if (bc.showCalculator) {
            const tokens = bc.calculatorTokens || []
            let text = ''
            tokens.forEach(token => {
              if (token.type === 'metric') text += `{${token.label}}`
              else text += token.value
            })
            bc.showCalculatorText = text
          }
        })
      })
    },

    buildCreateEventParams(eventDef) {
      const userAttributes = (eventDef.userAttributes || []).map(attr => {
        let conditionValue = []
        if (attr.typeValue === 'retention_user') {
          conditionValue = attr.value ? [attr.value] : []
        } else if (attr.value) {
          conditionValue = Array.isArray(attr.value) ? attr.value : [attr.value]
        }
        return {
          attributeType: attr.typeValue || '',
          comparisonOperator: attr.operatorValue || '',
          conditionValue: conditionValue,
        }
      })

      const behaviorConditions = (eventDef.behaviorGroups || []).map(group => {
        const conditions = group.conditions.map(bc => {
          const isIncrementRevenueType = this.isIncrementRevenueBehavior(bc)
          const windowDays = bc.windowType === 'unlimited' ? 0 : Number(bc.windowDays)
          const behaviorCondition = {
            behaviorType: bc.behaviorTypeValue || '',
            calculatorText: '',
            comparisonOperator: isIncrementRevenueType ? 'gte' : bc.operatorValue || '',
            conditionValue: [],
            gameplayNames: [],
            gameplayOperator: '',
            adzcodes: [],
            adzcodeOperator: '',
            modeNames: [],
            modeOperator: '',
            thresholdValue: this.buildThresholdValue(bc),
            timeDimension: bc.timeDimensionValue || '',
            windowDays,
            windowType: bc.windowType || 'unlimited',
          }

          const countryThresholds = this.buildCountryThresholds(
            bc,
            isIncrementRevenueType ? 'gte' : bc.operatorValue || '',
          )
          if (countryThresholds.length > 0) {
            behaviorCondition.countryThresholds = countryThresholds
          }

          if (bc.showCalculator && bc.showCalculatorText) {
            behaviorCondition.calculatorText = this.convertDisplayToCalculatorText(bc.showCalculatorText)
          }

          if (bc.subConditions && bc.subConditions.length > 0) {
            behaviorCondition.child = {
              logicType: bc.subConditionLogicType || 'and',
              conditions: bc.subConditions.map(subItem => {
                const isSubIncrementRevenue = this.isIncrementRevenueBehavior(subItem)
                return {
                  behaviorType: subItem.behaviorTypeValue || '',
                  comparisonOperator: isSubIncrementRevenue ? 'gte' : subItem.operatorValue || '',
                  conditionValue: [],
                  thresholdValue: this.buildThresholdValue(subItem),
                  timeDimension: bc.timeDimensionValue || '',
                  windowDays,
                  windowType: bc.windowType || 'unlimited',
                }
              }),
            }
          }

          if (bc.subFilters && bc.subFilters.length > 0) {
            bc.subFilters.forEach(sf => {
              const val = Array.isArray(sf.value) ? sf.value : sf.value ? [sf.value] : []
              if (sf.field === 'play_name') {
                behaviorCondition.gameplayNames = val
                behaviorCondition.gameplayOperator = sf.operator || ''
              } else if (sf.field === 'mode_name') {
                behaviorCondition.modeNames = val
                behaviorCondition.modeOperator = sf.operator || ''
              } else if (sf.field === 'ad_type') {
                behaviorCondition.adzcodes = val
                behaviorCondition.adzcodeOperator = sf.operator || ''
              }
            })
          }

          return behaviorCondition
        })

        return {
          conditionLogic: group.logicType || 'and',
          conditions,
        }
      })

      return {
        behaviorConditions,
        behaviorGroupLogic: eventDef.behaviorGroupLogicType || 'and',
        eventDisplayName: eventDef.name,
        eventName: eventDef.name,
        eventType: '',
        notes: eventDef.remark || '',
        reportWindowEnabled: Boolean(eventDef.reportWindowEnabled),
        ...(eventDef.reportWindowEnabled
          ? { reportWindowHours: Number(eventDef.reportWindowHours) }
          : {}),
        reportPlatforms: eventDef.reportPlatforms || [],
        status: this.status,
        userAttributes,
        ...this.buildAmplifyParams(eventDef),
      }
    },

    buildUpdateEventParams(eventDef) {
      const userAttributes = (eventDef.userAttributes || []).map(attr => {
        let conditionValue = []
        if (attr.typeValue === 'retention_user') {
          conditionValue = attr.value ? [attr.value] : []
        } else if (attr.value) {
          conditionValue = Array.isArray(attr.value) ? attr.value : [attr.value]
        }
        return {
          attributeType: attr.typeValue || '',
          comparisonOperator: attr.operatorValue || '',
          conditionValue: conditionValue,
        }
      })

      const behaviorConditions = (eventDef.behaviorGroups || []).map(group => {
        const conditions = group.conditions.map(bc => {
          const isIncrementRevenueType = this.isIncrementRevenueBehavior(bc)
          const windowDays = bc.windowType === 'unlimited' ? 0 : Number(bc.windowDays)
          const behaviorCondition = {
            behaviorType: bc.behaviorTypeValue || '',
            calculatorText: '',
            comparisonOperator: isIncrementRevenueType ? 'gte' : bc.operatorValue || '',
            conditionValue: [],
            gameplayNames: [],
            gameplayOperator: '',
            adzcodes: [],
            adzcodeOperator: '',
            modeNames: [],
            modeOperator: '',
            thresholdValue: this.buildThresholdValue(bc),
            timeDimension: bc.timeDimensionValue || '',
            windowDays,
            windowType: bc.windowType || 'unlimited',
          }

          const countryThresholds = this.buildCountryThresholds(
            bc,
            isIncrementRevenueType ? 'gte' : bc.operatorValue || '',
          )
          if (countryThresholds.length > 0) {
            behaviorCondition.countryThresholds = countryThresholds
          }

          if (bc.showCalculator && bc.showCalculatorText) {
            behaviorCondition.calculatorText = this.convertDisplayToCalculatorText(bc.showCalculatorText)
          }

          if (bc.subConditions && bc.subConditions.length > 0) {
            behaviorCondition.child = {
              logicType: bc.subConditionLogicType || 'and',
              conditions: bc.subConditions.map(subItem => {
                const isSubIncrementRevenue = this.isIncrementRevenueBehavior(subItem)
                return {
                  behaviorType: subItem.behaviorTypeValue || '',
                  comparisonOperator: isSubIncrementRevenue ? 'gte' : subItem.operatorValue || '',
                  conditionValue: [],
                  thresholdValue: this.buildThresholdValue(subItem),
                  timeDimension: bc.timeDimensionValue || '',
                  windowDays,
                  windowType: bc.windowType || 'unlimited',
                }
              }),
            }
          }

          if (bc.subFilters && bc.subFilters.length > 0) {
            bc.subFilters.forEach(sf => {
              const val = Array.isArray(sf.value) ? sf.value : sf.value ? [sf.value] : []
              if (sf.field === 'play_name') {
                behaviorCondition.gameplayNames = val
                behaviorCondition.gameplayOperator = sf.operator || ''
              } else if (sf.field === 'mode_name') {
                behaviorCondition.modeNames = val
                behaviorCondition.modeOperator = sf.operator || ''
              } else if (sf.field === 'ad_type') {
                behaviorCondition.adzcodes = val
                behaviorCondition.adzcodeOperator = sf.operator || ''
              }
            })
          }

          return behaviorCondition
        })

        return {
          conditionLogic: group.logicType || 'and',
          conditions,
        }
      })

      return {
        behaviorConditions,
        behaviorGroupLogic: eventDef.behaviorGroupLogicType || 'and',
        eventDisplayName: eventDef.name,
        eventName: eventDef.name,
        notes: eventDef.remark || '',
        reportWindowEnabled: Boolean(eventDef.reportWindowEnabled),
        ...(eventDef.reportWindowEnabled
          ? { reportWindowHours: Number(eventDef.reportWindowHours) }
          : {}),
        reportPlatforms: eventDef.reportPlatforms || [],
        status: this.status,
        userAttributes,
        ...this.buildAmplifyParams(eventDef),
      }
    },

    handleGoBack() {
      if (this.isViewMode || this.submitted) {
        this.$router.push({ name: 'AcquisitionEventManagement' })
        return
      }
      this.$confirm('确定要离开吗？未保存的内容将丢失。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          this.submitted = true
          this.$router.push({ name: 'AcquisitionEventManagement' })
        })
        .catch(() => {})
    },

    // ==================== 工具方法 ====================
    buildThresholdValue(item, comparisonOperator) {
      const operator = comparisonOperator || item.operatorValue
      if (this.isRangeOperator(operator)) {
        return {
          min: item.value ? Number(item.value) : 0,
          max: item.value2 ? Number(item.value2) : 0,
        }
      }
      if (this.isCollectionOperator(operator)) {
        return {
          value: this.getCollectionThresholdValues(item.value).map(value => {
            const numberValue = Number(value)
            return Number.isNaN(numberValue) ? value : numberValue
          }),
        }
      }
      return { value: item.value ? Number(item.value) : 0 }
    },

    buildAmplifyParams(eventDef) {
      const amplify = this.ensureAmplifyConfig(eventDef)
      const disabledAmplifyPolicy = {
        enabled: false,
        target: '',
        dimension: '',
        countryLevels: [],
      }
      if (!amplify.enabled) {
        return {
          amplifyPolicy: disabledAmplifyPolicy,
        }
      }
      const levelCount = amplify.defaultLevels.length
      const defaultLevels = this.formatAmplifyLevels(amplify.defaultLevels, levelCount)
      const countryLevels = (amplify.countryLevels || []).map(item => ({
        country: item.country,
        levels: this.formatAmplifyLevels(item.levels, levelCount),
      }))
      const amplifyPolicy = {
        enabled: true,
        target: amplify.target,
        dimension: amplify.dimension,
        countryLevels: [
          {
            country: '',
            levels: defaultLevels,
          },
          ...countryLevels,
        ],
      }
      return {
        amplifyPolicy,
      }
    },

    formatAmplifyLevels(levels, levelCount) {
      return (levels || [])
        .slice(0, levelCount || levels.length)
        .filter(level => !this.getAmplifyLevelValueState(level).isEmpty)
        .map(level => ({
          threshold: Number(level.threshold),
          multiplier: Number(level.multiplier),
        }))
    },

    convertDisplayToCalculatorText(text) {
      if (!text) return ''
      return text.replace(/\{([^}]+)\}/g, (match, eventLabel) => {
        const behavior = this.behaviorTypes.find(item => item.label === eventLabel)
        return behavior ? behavior.value : eventLabel
      })
    },

    parseDisplayTextToTokens(text) {
      if (!text) return []
      const tokens = []
      const operators = ['+', '-', '*', '/', '(', ')']
      const operatorDisplay = { '*': '×', '/': '÷' }
      let i = 0
      let currentText = ''

      while (i < text.length) {
        if (text[i] === '{') {
          if (currentText) {
            tokens.push({ type: 'text', value: currentText })
            currentText = ''
          }
          const endIndex = text.indexOf('}', i)
          if (endIndex !== -1) {
            const label = text.substring(i + 1, endIndex)
            const behavior = this.behaviorTypes.find(item => item.label === label)
            if (behavior) {
              tokens.push({ type: 'metric', value: behavior.value, label: behavior.label })
            } else {
              tokens.push({ type: 'text', value: `{${label}}` })
            }
            i = endIndex + 1
            continue
          }
        }

        if (operators.includes(text[i])) {
          if (currentText) {
            tokens.push({ type: 'text', value: currentText })
            currentText = ''
          }
          tokens.push({ type: 'operator', value: text[i], label: operatorDisplay[text[i]] || text[i] })
          i++
          continue
        }

        currentText += text[i]
        i++
      }

      if (currentText) {
        tokens.push({ type: 'text', value: currentText })
      }
      return tokens
    },

    normalizeAmplifyConfig(data) {
      const amplify = createEmptyAmplifyConfig()
      const source = data.amplifyPolicy || data.amplify || data.amplifyStrategy || data
      const enabled =
        source.amplifyEnabled !== undefined && source.amplifyEnabled !== null ? source.amplifyEnabled : source.enabled
      if (!enabled) return amplify

      amplify.enabled = true
      amplify.target = source.amplifyTarget || source.target || 'count'
      amplify.dimension = source.amplifyDimension || source.dimension || 'login_count'

      const countryLevels = source.amplifyCountryLevels || source.countryLevels || []
      const defaultCountryLevel = countryLevels.find(item => !item.country && !item.countryCode && !item.region)
      const defaultLevels = defaultCountryLevel
        ? defaultCountryLevel.levels || defaultCountryLevel.amplifyLevels || []
        : source.amplifyDefaultLevels || source.defaultLevels || source.amplifyLevels || source.levels || []
      const countryLevelCountList = countryLevels
        .filter(item => item.country || item.countryCode || item.region)
        .map(item => (item.levels || item.amplifyLevels || []).length)
      const levelCount = Math.max(defaultLevels.length, ...countryLevelCountList, 1)
      amplify.defaultLevels = this.normalizeAmplifyLevels(defaultLevels, levelCount)
      amplify.countryLevels = countryLevels
        .filter(item => item.country || item.countryCode || item.region)
        .map(item => ({
          country: item.country || item.countryCode || item.region || '',
          levels: this.normalizeAmplifyLevels(item.levels || item.amplifyLevels || [], levelCount),
        }))

      amplify.importDialogVisible = false
      amplify.importFile = null
      amplify.importFileName = ''
      amplify.importError = ''
      return amplify
    },

    normalizeReportPlatforms(platforms) {
      if (platforms === undefined || platforms === null || platforms === '') {
        return ['FIREBASE', 'AF']
      }
      const platformMap = {
        firebase: 'FIREBASE',
        firebaseapi: 'FIREBASE',
        fire_base: 'FIREBASE',
        af: 'AF',
        appsflyer: 'AF',
        app_flyer: 'AF',
        facebook: 'FACEBOOK',
        fb: 'FACEBOOK',
      }
      if (Array.isArray(platforms)) {
        return platforms.map(item => platformMap[String(item).toLowerCase()] || item).filter(Boolean)
      }
      if (typeof platforms === 'string') {
        return platforms
          .split(',')
          .map(item => item.trim())
          .map(item => platformMap[item.toLowerCase()] || item)
          .filter(Boolean)
      }
      return ['FIREBASE', 'AF']
    },
  },
}
</script>

<style lang="less" scoped>
@import './index.less';
</style>
<style lang="less">
.revenue-rule-tooltip {
  max-width: 520px;

  .revenue-rule-tooltip-content {
    white-space: pre-line;
    line-height: 1.7;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.85);
  }
}

.amplify-login-rule-tooltip {
  min-width: 660px;
  padding: 16px 18px;

  .amplify-login-rule-tooltip-content {
    line-height: 2.2;
    font-size: 14px;
    color: #000;
  }

  .amplify-login-rule-tooltip-title {
    margin-bottom: 4px;
    font-weight: 600;
  }
}
</style>

<style lang="less">
.event-selector-popover {
  padding: 0 !important;
  max-height: 300px;
  overflow: hidden;

  .event-selector-list {
    max-height: 300px;
    overflow-y: auto;

    .event-selector-item {
      padding: 8px 16px;
      font-size: 13px;
      color: rgba(0, 0, 0, 0.85);
      cursor: pointer;
      transition: all 0.3s;

      &:hover {
        background-color: #f5f5f5;
        color: #1890ff;
      }

      &:active {
        background-color: #e6f7ff;
      }
    }
  }
}

.amplify-import-dialog {
  border-radius: 2px;

  .el-dialog__header {
    padding: 24px 32px 12px;
    text-align: center;
  }

  .el-dialog__title {
    font-size: 18px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.85);
    line-height: 24px;
  }

  .el-dialog__headerbtn {
    top: 24px;
    right: 24px;
    width: 20px;
    height: 20px;
    font-size: 24px;

    .el-dialog__close {
      color: rgba(0, 0, 0, 0.45);
      font-weight: 300;
    }

    &:hover .el-dialog__close {
      color: #1890ff;
    }
  }

  .el-dialog__body {
    padding: 12px 32px 0;
  }

  .el-dialog__footer {
    padding: 24px 32px 24px;
  }

  .dialog-footer,
  .el-dialog__footer > span {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .el-dialog__footer .el-button {
    min-width: 60px;
    height: 32px;
    padding: 0 16px;
    border-radius: 2px;
    font-size: 14px;
  }

  .amplify-import-upload {
    display: block;

    .el-upload {
      display: block;
      width: 100%;
    }

    .el-upload-dragger {
      display: flex;
      width: 100%;
      height: 180px;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border: 1px dashed #1890ff;
      border-radius: 4px;
      background: #fff;

      &:hover {
        border-color: #40a9ff;
      }
    }

    .el-icon-upload {
      margin: 0 0 12px;
      color: #b8bec9;
      font-size: 52px;
      line-height: 1;
    }

    .el-upload__text {
      color: rgba(0, 0, 0, 0.65);
      font-size: 14px;
      line-height: 22px;
    }
  }

  .amplify-import-tip {
    margin-top: 12px;
    color: rgba(0, 0, 0, 0.65);
    font-size: 13px;
    line-height: 20px;
  }

  .amplify-import-error {
    margin: 8px 0 0;
    color: #f5222d;
    font-size: 13px;
    line-height: 20px;
  }
}
</style>
